import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Bus, Navigation, MapPin, ExternalLink, AlertCircle, Gauge, Fuel,
  User, Clock, Radio, Search, Activity, Layers, Crosshair,
  Video, Phone, Megaphone, Thermometer, BatteryFull, Users, Route as RouteIcon,
  ShieldCheck, Camera, CircleDot, Wifi, Snowflake, X, Maximize2, Volume2,
  TriangleAlert, ChevronRight, Disc, Mic, Lock, History as HistoryIcon, Play, Pause, RotateCcw,
  School,
} from "lucide-react";


import { loadGoogleMaps } from "@/lib/google-maps-loader";
import { SCHOOLS, BUSES, BUSES_BY_SCHOOL, SCHOOL_BY_ID, NEIGHBORHOODS, CATCHMENT_POLYGONS, type SchoolDef, type BusDef, type BusStatus, STATUS_COLORS } from "@/lib/transit-data";
const loadMapsApi = loadGoogleMaps;



function busSvg(color: string, heading = 0, selected = false) {
  const ring = selected ? `<circle cx='22' cy='22' r='20' fill='none' stroke='${color}' stroke-width='2' opacity='0.6'/>` : "";
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 48 48'>
    ${ring}
    <g transform='rotate(${heading} 24 24)'>
      <path d='M24 4 L34 22 H14 Z' fill='${color}' opacity='0.35'/>
      <circle cx='24' cy='24' r='14' fill='${color}' stroke='white' stroke-width='2.5'/>
      <path d='M17 21a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2v1a1 1 0 0 1-2 0v-1H21v1a1 1 0 0 1-2 0v-1a2 2 0 0 1-2-2v-6zm2 1v3h10v-3H19zm2 6a1.1 1.1 0 1 0 0-2.2A1.1 1.1 0 0 0 21 28zm8 0a1.1 1.1 0 1 0 0-2.2A1.1 1.1 0 0 0 29 28z' fill='white'/>
    </g>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

type BusPos = {
  id: string; route: string; lat: number; lng: number; heading: number | null;
  speed_kmh: number | null; next_stop: string | null; eta_minutes: number | null; captured_at: string;
};

// Color lookup now uses school colors from transit-data
const colorFor = (route: string): string => {
  const bus = BUSES.find(b => b.vehicleNumber === route);
  if (bus) return bus.color;
  const school = SCHOOLS.find(s => s.id === route);
  if (school) return school.color;
  return "#6B5535";
};

// Route stops now come from the transit-data BusDef
const ROUTE_STOPS: Record<string, Array<{ label: string; lat: number; lng: number }>> = {};
for (const bus of BUSES) {
  ROUTE_STOPS[bus.vehicleNumber] = [
    { label: bus.garage.label, lat: bus.garage.lat, lng: bus.garage.lng },
    ...bus.stops.map(s => ({ label: s.label, lat: s.lat, lng: s.lng })),
    { label: bus.schoolLabel, lat: bus.schoolLocation.lat, lng: bus.schoolLocation.lng },
  ];
}

// Premium light map theme — clean, Apple Maps / Linear inspired
const LIGHT_MAP_STYLES: any[] = [
  { elementType: "geometry", stylers: [{ color: "#f5f6f8" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#5f6b7a" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
  { elementType: "labels.icon", stylers: [{ saturation: -60, lightness: 10 }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#e2e6ec" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#4a5568" }] },
  { featureType: "poi", stylers: [{ visibility: "simplified" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#e8f0e3" }] },
  { featureType: "poi.school", elementType: "geometry", stylers: [{ color: "#eef2f7" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#8b95a3" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#eef0f3" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#e4e8ed" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#d1d6dc" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#6a7585" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#dceaf2" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#7ba0b8" }] },
  { featureType: "landscape", stylers: [{ color: "#f0f2f5" }] },
  { featureType: "landscape.natural", stylers: [{ color: "#edf0e8" }] },
];

// Derived telemetry from transit-data BusDef — stable per bus route
function hash(s: string) { let h = 0; for (let i=0;i<s.length;i++) h = ((h<<5) - h + s.charCodeAt(i)) | 0; return Math.abs(h); }
function telemetry(p: BusPos) {
  const h = hash(p.id);
  const busDef = BUSES.find(b => b.vehicleNumber === p.route);
  const school = busDef ? SCHOOL_BY_ID[busDef.schoolId] : null;
  return {
    driver: busDef?.driver ?? "Unassigned",
    conductor: busDef?.conductor ?? "—",
    plate: busDef?.plate ?? "UAR 000X",
    fleetNumber: busDef?.fleetNumber ?? "TST-0000",
    fuel: 35 + (h % 60),
    mileage: 84_000 + (h % 50_000),
    students: 18 + (h % 28),
    capacity: busDef?.capacity ?? 52,
    engineOn: (p.speed_kmh ?? 0) > 0.5,
    ignition: (p.speed_kmh ?? 0) > 0.5 ? "ON" : "IDLE",
    schoolName: school?.name ?? "—",
    schoolId: busDef?.schoolId ?? "",
    routeName: busDef?.routeName ?? "—",
  };
}

export function LiveBusMap({
  routes,
  height = 360,
  autoTick = false,
  title = "Live bus tracking",
  wialon = false,
  canViewTrails = false,
  canViewHistory = false,
  maxBuses,
}: {
  routes?: string[];
  height?: number;
  autoTick?: boolean;
  title?: string;
  wialon?: boolean;
  /** Show live breadcrumb polylines behind each bus. Off for parents/students by default. */
  canViewTrails?: boolean;
  /** Allow opening the admin-only route history replay from the vehicle modal. */
  canViewHistory?: boolean;
  /** Cap the number of buses displayed — picks the first N by route order. */
  maxBuses?: number;
}) {
  const qc = useQueryClient();
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<Record<string, any>>({});
  const trailsRef = useRef<Record<string, any>>({});
  const trailCoordsRef = useRef<Record<string, Array<{ lat: number; lng: number }>>>({});
  const plannedLinesRef = useRef<Record<string, any[]>>({});
  const destMarkersRef = useRef<Record<string, any>>({});
  const trafficLayerRef = useRef<any>(null);
  const pathsRef = useRef<Record<string, {
    coords: { lat: number; lng: number }[]; idx: number;
    dest: { lat: number; lng: number; label: string };
    total: number; traveled: number;
    freeFlowKmh: number; trafficKmh: number; trafficFactor: number;
    intervals: Array<{ start: number; end: number; speed: string; speedKmh?: number }>;
  }>>({});
  const fetchingRef = useRef<Set<string>>(new Set());
  const positionsRef = useRef<BusPos[]>([]);
  // Per-bus route state: tracks which stop the bus is heading to next
  // Route sequence: garage → stop[0] → stop[1] → ... → school
  const routeStateRef = useRef<Record<string, { stopIdx: number; phase: "to_stop" | "at_stop" | "to_school" | "at_school" | "done"; stopTimer: number }>>({});
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "moving" | "idle" | "alert">("all");
  const [routeFilter, setRouteFilter] = useState<string | null>(null);
  const [trailsOn, setTrailsOn] = useState(false);
  // Unique channel ID per component instance to avoid "cannot add postgres_changes callbacks after subscribe"
  const channelIdRef = useRef(Math.random().toString(36).slice(2, 10));
  const [trafficOn, setTrafficOn] = useState(true);
  const [historyBus, setHistoryBus] = useState<BusPos | null>(null);
  // ── Catchment area overlay state ──
  const catchmentRef = useRef<any>(null);        // the polygon
  const catchmentStopsRef = useRef<any[]>([]);   // stop markers within the zone
  const catchmentRoutesRef = useRef<any[]>([]);  // route polylines for the school
  const allCatchmentsRef = useRef<any[]>([]);    // all school polygons when toggle is on
  const [selectedSchoolId, setSelectedSchoolId] = useState<string | null>(null);
  const [showCatchments, setShowCatchments] = useState(false);
  const [catchmentOnSelect, setCatchmentOnSelect] = useState(true);
  // Trails are strictly gated: parents/students never see the live breadcrumb line.
  const showTrails = canViewTrails && trailsOn;


  const openDetail = (route: string) => { setSelected(route); setDetailOpen(true); };


  const { data: positions = [] } = useQuery<BusPos[]>({
    queryKey: ["bus-positions-live", routes?.join(",") ?? "all", maxBuses ?? "all"],
    refetchInterval: 3000,
    queryFn: async () => {
      let q: any = supabase.from("bus_positions" as any).select("*");
      if (routes && routes.length) q = q.in("route", routes);
      const { data } = await q.order("captured_at", { ascending: false });
      let rows = (data as BusPos[]) ?? [];
      if (maxBuses && rows.length > maxBuses) {
        const seen = new Set<string>();
        const limited: BusPos[] = [];
        for (const r of rows) {
          if (!seen.has(r.route)) { seen.add(r.route); limited.push(r); }
          if (limited.length >= maxBuses) break;
        }
        return limited;
      }
      return rows;
    },
  });

  // Init map
  useEffect(() => {
    let cancelled = false;
    loadMapsApi().then(() => {
      if (cancelled || !ref.current) return;
      const KAMPALA = { lat: 0.3476, lng: 32.5825 };
      mapRef.current = new window.google.maps.Map(ref.current, {
        center: KAMPALA, zoom: 12, disableDefaultUI: true, zoomControl: true, clickableIcons: false,
        styles: wialon ? LIGHT_MAP_STYLES : [
          { featureType: "poi", stylers: [{ visibility: "off" }] },
          { featureType: "transit", stylers: [{ visibility: "off" }] },
        ],
        backgroundColor: wialon ? "#f5f6f8" : undefined,
      });
      trafficLayerRef.current = new window.google.maps.TrafficLayer();
      trafficLayerRef.current.setMap(mapRef.current);
      setReady(true);
    }).catch(e => setErr(e.message));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recenter map on fleet centroid once positions arrive
  useEffect(() => {
    if (!ready || !mapRef.current || positions.length === 0) return;
    const g = window.google.maps;
    const bounds = new g.LatLngBounds();
    for (const p of positions) bounds.extend({ lat: p.lat, lng: p.lng });
    if (positions.length === 1) {
      mapRef.current.panTo({ lat: positions[0].lat, lng: positions[0].lng });
      mapRef.current.setZoom(15);
    } else {
      mapRef.current.fitBounds(bounds, 80);
    }
    // Only recenter once on first load — don't fight user pan afterwards
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, positions.length > 0]);

  // Toggle the Google live traffic overlay
  useEffect(() => {
    if (!ready || !trafficLayerRef.current || !mapRef.current) return;
    trafficLayerRef.current.setMap(trafficOn ? mapRef.current : null);
  }, [trafficOn, ready]);

  // Realtime subscription — only when NOT auto-ticking (motion engine handles updates locally)
  useEffect(() => {
    if (autoTick) return;
    const ch = supabase.channel(`bus-pos-${channelIdRef.current}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "bus_positions" }, () => {
        qc.invalidateQueries({ queryKey: ["bus-positions-live"] });
      }).subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [qc, autoTick]);

  // Sync markers
  useEffect(() => {
    if (!ready || !mapRef.current || !window.google) return;
    const g = window.google.maps;
    const seen = new Set<string>();
    const bounds = new g.LatLngBounds();
    let hasPoints = false;

    for (const p of positions) {
      seen.add(p.route);
      const pos = { lat: p.lat, lng: p.lng };
      hasPoints = true;
      bounds.extend(pos);
      const isSel = selected === p.route;

      let m = markersRef.current[p.route];
      if (!m) {
        m = new g.Marker({
          map: mapRef.current, position: pos, title: p.route, zIndex: isSel ? 99 : 10,
          icon: { url: busSvg(colorFor(p.route), p.heading ?? 0, isSel), scaledSize: new g.Size(48, 48), anchor: new g.Point(24, 24) },
        });
        m.addListener("click", () => openDetail(p.route));
        markersRef.current[p.route] = m;
      } else {
        const prev = m.getPosition();
        const steps = 12;
        const fromLat = prev.lat(), fromLng = prev.lng();
        const dLat = (pos.lat - fromLat) / steps, dLng = (pos.lng - fromLng) / steps;
        let i = 0;
        const tickAnim = () => {
          i++;
          m.setPosition({ lat: fromLat + dLat * i, lng: fromLng + dLng * i });
          if (i < steps) requestAnimationFrame(tickAnim);
        };
        if (fromLat !== pos.lat || fromLng !== pos.lng) tickAnim();
        m.setIcon({ url: busSvg(colorFor(p.route), p.heading ?? 0, isSel), scaledSize: new g.Size(48, 48), anchor: new g.Point(24, 24) });
      }

      const coords = trailCoordsRef.current[p.route] ?? [];
      const last = coords[coords.length - 1];
      if (!last || last.lat !== pos.lat || last.lng !== pos.lng) {
        coords.push(pos);
        if (coords.length > 60) coords.shift();
        trailCoordsRef.current[p.route] = coords;
      }
      // Trails are gated: parents/students never see a breadcrumb path.
      if (canViewTrails) {
        let line = trailsRef.current[p.route];
        if (!line) {
          line = new g.Polyline({
            map: showTrails ? mapRef.current : null, path: coords, geodesic: true,
            strokeColor: colorFor(p.route), strokeOpacity: 0.8, strokeWeight: 4,
            icons: wialon ? [{ icon: { path: g.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2, strokeColor: colorFor(p.route) }, offset: "100%", repeat: "120px" }] : undefined,
          });
          trailsRef.current[p.route] = line;
        } else {
          line.setPath(coords);
          line.setMap(showTrails ? mapRef.current : null);
        }
      } else if (trailsRef.current[p.route]) {
        // Belt-and-braces: if the caller flips permission off, wipe existing lines.
        trailsRef.current[p.route].setMap(null);
        delete trailsRef.current[p.route];
      }
    }

    for (const route of Object.keys(markersRef.current)) {
      if (!seen.has(route)) {
        markersRef.current[route].setMap(null);
        delete markersRef.current[route];
        trailsRef.current[route]?.setMap(null);
        delete trailsRef.current[route];
        delete trailCoordsRef.current[route];
      }
    }


    if (hasPoints && Object.keys(markersRef.current).length > 1 && selected) {
      const sp = positions.find(p => p.route === selected);
      if (sp) mapRef.current.panTo({ lat: sp.lat, lng: sp.lng });
    } else if (hasPoints && selected) {
      const sp = positions.find(p => p.route === selected);
      if (sp) mapRef.current.panTo({ lat: sp.lat, lng: sp.lng });
    } else if (hasPoints) {
      mapRef.current.panTo({ lat: positions[0].lat, lng: positions[0].lng });
    }
  }, [positions, ready, selected, trailsOn, wialon]);

  // Keep latest positions accessible inside the async tick loop
  useEffect(() => { positionsRef.current = positions; }, [positions]);

  // ============================================================
  // ROAD-FOLLOWING MOTION ENGINE
  // Each bus is assigned a real driving polyline from Google Routes
  // (snapped to actual roads). On every tick we advance the bus along
  // that polyline at its current speed, computing heading from the
  // segment bearing. When the polyline is exhausted, we fetch a fresh
  // leg from the current position to a new nearby destination.
  // ============================================================
  useEffect(() => {
    if (!autoTick || !ready) return;
    let cancelled = false;
    const intervalMs = 1500;

    const toRad = (d: number) => (d * Math.PI) / 180;
    const haversineM = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
      const R = 6371000;
      const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
      const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
      return 2 * R * Math.asin(Math.sqrt(s));
    };
    const bearingDeg = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
      const φ1 = toRad(a.lat), φ2 = toRad(b.lat), Δλ = toRad(b.lng - a.lng);
      const y = Math.sin(Δλ) * Math.cos(φ2);
      const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
      return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
    };
    const movePoint = (lat: number, lng: number, distKm: number, bearing: number) => {
      const R = 6371, δ = distKm / R, θ = toRad(bearing);
      const φ1 = toRad(lat), λ1 = toRad(lng);
      const φ2 = Math.asin(Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ));
      const λ2 = λ1 + Math.atan2(Math.sin(θ) * Math.sin(δ) * Math.cos(φ1), Math.cos(δ) - Math.sin(φ1) * Math.sin(φ2));
      return { lat: (φ2 * 180) / Math.PI, lng: (((λ2 * 180) / Math.PI) + 540) % 360 - 180 };
    };

    // Route state helpers — routeStateRef is at component level
    function getRouteState(busId: string, busRoute: string) {
      if (!routeStateRef.current[busId]) {
        const busDef = BUSES.find(b => b.vehicleNumber === busRoute);
        routeStateRef.current[busId] = { stopIdx: 0, phase: "to_stop", stopTimer: 0 };
      }
      return routeStateRef.current[busId];
    }

    function getNextDestination(p: BusPos): { lat: number; lng: number; label: string } | null {
      const busDef = BUSES.find(b => b.vehicleNumber === p.route);
      if (!busDef) return null;
      const rs = getRouteState(p.id, p.route);
      if (rs.phase === "to_stop" && rs.stopIdx < busDef.stops.length) {
        const stop = busDef.stops[rs.stopIdx];
        return { lat: stop.lat, lng: stop.lng, label: stop.label };
      }
      if (rs.phase === "to_school") {
        return { lat: busDef.schoolLocation.lat, lng: busDef.schoolLocation.lng, label: busDef.schoolLabel };
      }
      return null;
    }

    async function ensurePath(p: BusPos) {
      const existing = pathsRef.current[p.id];
      if (existing && existing.idx < existing.coords.length - 1) return existing;
      if (fetchingRef.current.has(p.id)) return null;
      fetchingRef.current.add(p.id);
      try {
        const { getRoadRoute } = await import("@/lib/mobility-engine");
        const busDef = BUSES.find(b => b.vehicleNumber === p.route);

        let dest: { lat: number; lng: number; label: string };
        if (busDef) {
          const next = getNextDestination(p);
          if (!next) {
            // Route complete — park at school
            dest = { lat: busDef.schoolLocation.lat, lng: busDef.schoolLocation.lng, label: "At School" };
          } else {
            dest = next;
          }
        } else {
          // Fallback for unknown routes
          const bearing = (((p.heading ?? Math.random() * 360) + (Math.random() - 0.5) * 90) + 360) % 360;
          const distKm = 2 + Math.random() * 4;
          const mp = movePoint(p.lat, p.lng, distKm, bearing);
          dest = { ...mp, label: p.next_stop ?? "En route" };
        }

        const route = await getRoadRoute(
          { lat: p.lat, lng: p.lng },
          { lat: dest.lat, lng: dest.lng }
        );

        const decoded = route.geometry;
        if (decoded.length < 2) return null;

        const distKmTotal = route.distanceMeters / 1000;
        const durationSec = route.durationSeconds;
        const trafficKmh = durationSec > 0 ? (distKmTotal / (durationSec / 3600)) : 35;
        const freeFlowKmh = trafficKmh * 1.3;
        const trafficFactor = 0.77;

        const intervals = route.roadSegments.map((seg, idx) => ({
          start: idx,
          end: idx + 1,
          speed: seg.speedLimit < 25 ? "TRAFFIC_JAM" : seg.speedLimit < 40 ? "SLOW" : "NORMAL",
          speedKmh: seg.speedLimit,
        }));

        const st = {
          coords: decoded,
          idx: 0,
          dest: { ...dest, label: dest.label },
          total: route.distanceMeters,
          traveled: 0,
          freeFlowKmh,
          trafficKmh,
          trafficFactor,
          intervals,
        };
        pathsRef.current[p.id] = st;

        // Draw the planned road polyline
        const g = window.google.maps;
        plannedLinesRef.current[p.id]?.forEach((l: any) => l?.setMap?.(null));

        const baseColor = colorFor(p.route);
        const roadColor = (seg: typeof route.roadSegments[0]) => {
          if (seg.speedLimit < 25) return "#B33A1A";
          if (seg.speedLimit < 35) return "#f59e0b";
          if (seg.roadClass === "highway") return "#10b981";
          if (seg.roadClass === "primary") return "#3b82f6";
          return baseColor;
        };

        const lines: any[] = [];
        for (let i = 0; i < decoded.length - 1; i++) {
          const seg = route.roadSegments[i];
          if (!seg) continue;
          const slice = decoded.slice(i, i + 2);
          lines.push(new g.Polyline({
            map: mapRef.current,
            path: slice,
            geodesic: true,
            strokeColor: roadColor(seg),
            strokeOpacity: seg.roadClass === "highway" || seg.roadClass === "primary" ? 0.7 : 0.5,
            strokeWeight: seg.roadClass === "highway" ? 7 : seg.roadClass === "primary" ? 6 : 4,
            zIndex: seg.roadClass === "highway" ? 3 : 1,
          }));
        }
        plannedLinesRef.current[p.id] = lines;

        // Destination marker
        destMarkersRef.current[p.id]?.setMap(null);
        const isSchool = busDef && dest.lat === busDef.schoolLocation.lat && dest.lng === busDef.schoolLocation.lng;
        destMarkersRef.current[p.id] = new g.Marker({
          map: mapRef.current,
          position: decoded[decoded.length - 1],
          icon: isSchool ? {
            path: g.SymbolPath.BACKWARD_CLOSED_ARROW,
            scale: 5,
            fillColor: baseColor,
            fillOpacity: 1,
            strokeColor: "#fff",
            strokeWeight: 2,
          } : {
            path: g.SymbolPath.CIRCLE,
            scale: 6,
            fillColor: baseColor,
            fillOpacity: 1,
            strokeColor: "#fff",
            strokeWeight: 2,
          },
          zIndex: 2,
        });

        return st;
      } catch (err) {
        console.warn("Road route fetch failed:", err);
        return null;
      } finally {
        fetchingRef.current.delete(p.id);
      }
    }

    // Returns the local traffic multiplier (0.2..1) for the bus's current polyline index
    function localTrafficMult(st: { intervals: Array<{ start: number; end: number; speed: string }> }, idx: number) {
      const iv = st.intervals.find(i => idx >= i.start && idx <= i.end);
      if (!iv) return 1;
      if (iv.speed === "TRAFFIC_JAM") return 0.25;
      if (iv.speed === "SLOW") return 0.55;
      return 1;
    }

    async function step() {
      if (cancelled) return;
      const list = positionsRef.current;
      const writes: Promise<any>[] = [];
      for (const p of list) {
        const st = await ensurePath(p);
        if (!st) continue;
        // Traffic-aware base speed: target the leg's avg speed, then modulate by local congestion
        const base = Math.max(8, Math.min(70, st.trafficKmh));
        const local = localTrafficMult(st, st.idx);
        const speed = Math.max(3, base * local + (Math.random() - 0.5) * 4);
        let remaining = (speed * (intervalMs / 1000)) / 3.6;
        let curLat = p.lat, curLng = p.lng;
        let heading = p.heading ?? 0;
        while (remaining > 0 && st.idx < st.coords.length - 1) {
          const a = { lat: curLat, lng: curLng };
          const b = st.coords[st.idx + 1];
          const segDist = haversineM(a, b);
          if (segDist <= remaining) {
            remaining -= segDist;
            heading = bearingDeg(a, b);
            curLat = b.lat; curLng = b.lng;
            st.idx++;
            st.traveled += segDist;
          } else {
            const t = remaining / segDist;
            heading = bearingDeg(a, b);
            curLat = a.lat + (b.lat - a.lat) * t;
            curLng = a.lng + (b.lng - a.lng) * t;
            st.traveled += remaining;
            remaining = 0;
          }
        }
        const progress = Math.min(1, st.traveled / Math.max(1, st.total));
        // ETA = remaining distance / leg's traffic-aware avg speed
        const remainKm = Math.max(0, (st.total - st.traveled) / 1000);
        const eta = Math.max(0, Math.round((remainKm / Math.max(8, st.trafficKmh)) * 60));
        writes.push(
          (supabase.from as any)("bus_positions").update({
            lat: curLat, lng: curLng, heading, speed_kmh: speed,
            eta_minutes: eta,
            next_stop: st.dest.label,
            captured_at: new Date().toISOString(),
          }).eq("id", p.id)
        );
        if (st.idx >= st.coords.length - 1 || progress >= 0.995) {
          // Advance route state: stop → next stop → ... → school → done
          const busDef = BUSES.find(b => b.vehicleNumber === p.route);
          if (busDef) {
            const rs = getRouteState(p.id, p.route);
            if (rs.phase === "to_stop") {
              rs.stopIdx++;
              if (rs.stopIdx >= busDef.stops.length) {
                rs.phase = "to_school";
              }
            } else if (rs.phase === "to_school") {
              rs.phase = "at_school";
              // After a pause at school, mark done
              setTimeout(() => {
                if (routeStateRef.current[p.id]) {
                  routeStateRef.current[p.id].phase = "done";
                }
              }, 5000);
            }
          }
          plannedLinesRef.current[p.id]?.forEach((l: any) => l?.setMap?.(null));
          destMarkersRef.current[p.id]?.setMap(null);
          delete plannedLinesRef.current[p.id];
          delete destMarkersRef.current[p.id];
          delete pathsRef.current[p.id];
        }
      }
      await Promise.all(writes).catch(() => {});
      if (!cancelled) setTimeout(step, intervalMs);
    }
    const t = setTimeout(step, intervalMs);
    return () => {
      cancelled = true;
      clearTimeout(t);
      Object.values(plannedLinesRef.current).forEach((arr: any) => arr?.forEach?.((l: any) => l?.setMap?.(null)));
      Object.values(destMarkersRef.current).forEach((m: any) => m?.setMap?.(null));
      plannedLinesRef.current = {};
      destMarkersRef.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoTick, ready]);

  // Draw school markers on the map
  // ── Show catchment zone for a school ──
  // Draws the service polygon, highlights all stops within the zone, and
  // traces the bus routes that serve this school. When called with null,
  // clears the overlay.
  const showSchoolCatchment = useCallback((schoolId: string | null) => {
    const g = (window as any).google?.maps;
    const map = mapRef.current;
    if (!g || !map) return;

    // Clear previous overlay
    catchmentRef.current?.setMap(null);
    catchmentRef.current = null;
    catchmentStopsRef.current.forEach((s: any) => s?.setMap?.(null));
    catchmentStopsRef.current = [];
    catchmentRoutesRef.current.forEach((r: any) => r?.setMap?.(null));
    catchmentRoutesRef.current = [];

    if (!schoolId) {
      setSelectedSchoolId(null);
      return;
    }

    const school = SCHOOL_BY_ID[schoolId];
    if (!school) return;
    setSelectedSchoolId(schoolId);

    // 1. Draw catchment polygon
    const polyCoords = CATCHMENT_POLYGONS[schoolId];
    if (polyCoords && polyCoords.length >= 3) {
      catchmentRef.current = new g.Polygon({
        paths: polyCoords.map(p => new g.LatLng(p.lat, p.lng)),
        strokeColor: school.color,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: school.color,
        fillOpacity: 0.08,
        map,
      });
      // Fit bounds to the polygon
      const bounds = new g.LatLngBounds();
      polyCoords.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
      map.fitBounds(bounds, 60);
    }

    // 2. Draw all stops for buses serving this school
    const schoolBuses = BUSES_BY_SCHOOL[schoolId] || [];
    const stopSet = new Set<string>();
    for (const bus of schoolBuses) {
      for (const stop of bus.stops) {
        const key = `${stop.lat.toFixed(4)},${stop.lng.toFixed(4)}`;
        if (stopSet.has(key)) continue;
        stopSet.add(key);
        const m = new g.Marker({
          position: { lat: stop.lat, lng: stop.lng },
          map,
          icon: {
            path: g.SymbolPath.CIRCLE,
            scale: 4,
            fillColor: school.color,
            fillOpacity: 0.9,
            strokeColor: "#fff",
            strokeWeight: 1.5,
          },
          title: stop.label,
        });
        const iw = new g.InfoWindow({ content: `<div style="font-family:system-ui;font-size:11px"><b>${stop.label}</b><br/>Pickup stop · ${school.name}</div>` });
        m.addListener("click", () => iw.open(map, m));
        catchmentStopsRef.current.push(m);
      }
    }

    // 3. Draw route polylines for this school's buses
    for (const bus of schoolBuses) {
      const path = [
        { lat: bus.garage.lat, lng: bus.garage.lng },
        ...bus.stops.map(s => ({ lat: s.lat, lng: s.lng })),
        { lat: bus.schoolLocation.lat, lng: bus.schoolLocation.lng },
      ];
      const line = new g.Polyline({
        path: path.map(p => new g.LatLng(p.lat, p.lng)),
        geodesic: true,
        strokeColor: school.color,
        strokeOpacity: 0.45,
        strokeWeight: 2,
        map,
      });
      catchmentRoutesRef.current.push(line);
    }
  }, []);

  // ── Auto-show catchment when a bus is selected ──
  useEffect(() => {
    if (!catchmentOnSelect || !selected) return;
    const bus = BUSES.find(b => b.routeName === selected);
    if (bus) showSchoolCatchment(bus.schoolId);
  }, [selected, catchmentOnSelect, showSchoolCatchment]);

  // ── Show all catchment zones when the toggle is on ──
  useEffect(() => {
    const g = (window as any).google?.maps;
    const map = mapRef.current;
    if (!g || !map) return;
    // Clear existing
    allCatchmentsRef.current.forEach((p: any) => p?.setMap?.(null));
    allCatchmentsRef.current = [];
    if (!showCatchments) return;
    for (const school of SCHOOLS) {
      const polyCoords = CATCHMENT_POLYGONS[school.id];
      if (!polyCoords || polyCoords.length < 3) continue;
      const poly = new g.Polygon({
        paths: polyCoords.map(p => new g.LatLng(p.lat, p.lng)),
        strokeColor: school.color,
        strokeOpacity: 0.5,
        strokeWeight: 1.5,
        fillColor: school.color,
        fillOpacity: 0.06,
        map,
      });
      poly.addListener("click", () => showSchoolCatchment(school.id));
      allCatchmentsRef.current.push(poly);
    }
  }, [showCatchments, mapRef.current, showSchoolCatchment]);

  const schoolMarkersRef = useRef<Record<string, any>>({});
  const schoolOverlaysRef = useRef<Record<string, any>>({});
  const [showSchools, setShowSchools] = useState(true);
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    let cancelled = false;
    (async () => {
      await loadMapsApi();
      if (cancelled || !mapRef.current) return;
      const g = window.google.maps;
      // Clear existing markers and overlays
      Object.values(schoolMarkersRef.current).forEach((m: any) => m?.setMap?.(null));
      Object.values(schoolOverlaysRef.current).forEach((o: any) => o?.setMap?.(null));
      schoolMarkersRef.current = {};
      schoolOverlaysRef.current = {};

      for (const school of SCHOOLS) {
        // ── Innovative 3D campus locator ──
        // Uses a custom OverlayView to render an HTML-based marker with
        // a pulsing ring, a 3D building icon, and a floating label.
        const latLng = new g.LatLng(school.lat, school.lng);

        // Invisible marker for positioning and click handling
        const marker = new g.Marker({
          map: showSchools ? mapRef.current : null,
          position: latLng,
          icon: {
            path: g.SymbolPath.CIRCLE,
            scale: 0,
            fillOpacity: 0,
            strokeOpacity: 0,
          },
          zIndex: 10,
          title: school.name,
        });

        // Custom HTML overlay for the campus pin
        class CampusOverlay extends g.OverlayView {
          div: HTMLDivElement | null = null;
          schoolData: any;
          onSchoolClick: (id: string) => void;
          _map: any;

          constructor(map: any, schoolData: any, onClick: (id: string) => void) {
            super();
            this.schoolData = schoolData;
            this.onSchoolClick = onClick;
            this.setMap(map);
          }

          onAdd() {
            this.div = document.createElement("div");
            this.div.style.cssText = "position:absolute;cursor:pointer;transform:translate(-50%,-100%);transition:transform 0.2s ease;will-change:transform";
            this.div.innerHTML = `
              <div style="position:relative;display:flex;flex-direction:column;align-items:center;filter:drop-shadow(0 4px 8px rgba(0,0,0,0.3))">
                <div style="position:absolute;top:-6px;width:44px;height:44px;border-radius:50%;background:${school.color}22;animation:campusPulse 2.5s ease-out infinite"></div>
                <div style="position:absolute;top:-3px;width:34px;height:34px;border-radius:50%;background:${school.color}33;animation:campusPulse 2.5s ease-out infinite 0.5s"></div>
                <div style="position:relative;width:32px;height:40px;display:flex;align-items:flex-start;justify-content:center">
                  <svg width="32" height="40" viewBox="0 0 32 40" style="position:absolute;top:0;left:0">
                    <defs>
                      <linearGradient id="grad-${school.id}" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stop-color="${school.color}" stop-opacity="1"/>
                        <stop offset="100%" stop-color="${school.color}" stop-opacity="0.75"/>
                      </linearGradient>
                    </defs>
                    <path d="M16 0 C7 0 2 5 2 12 C2 18 6 22 10 26 L16 40 L22 26 C26 22 30 18 30 12 C30 5 25 0 16 0 Z" fill="url(#grad-${school.id})" stroke="#fff" stroke-width="1.5"/>
                    <circle cx="16" cy="12" r="8" fill="#fff" opacity="0.95"/>
                    <text x="16" y="16" text-anchor="middle" font-size="11" font-weight="bold" fill="${school.color}" font-family="system-ui">${school.logo}</text>
                  </svg>
                </div>
                <div style="margin-top:-2px;background:#fff;border-radius:6px;padding:2px 8px;font-size:9px;font-weight:600;color:${school.color};box-shadow:0 1px 3px rgba(0,0,0,0.15);white-space:nowrap;max-width:120px;overflow:hidden;text-overflow:ellipsis">${school.name.replace(" Academy","").replace(" International School"," Int'l").replace(" Primary School"," PS").replace(" High School"," HS")}</div>
              </div>
            `;
            this.div.addEventListener("click", () => this.onSchoolClick(this.schoolData.id));
            this.div.addEventListener("mouseenter", () => {
              if (this.div) this.div.style.transform = "translate(-50%,-100%) scale(1.15)";
            });
            this.div.addEventListener("mouseleave", () => {
              if (this.div) this.div.style.transform = "translate(-50%,-100%) scale(1)";
            });
            const panes = this.getPanes();
            if (panes) panes.floatPane.appendChild(this.div);
          }

          draw() {
            if (!this.div) return;
            const projection = this.getProjection();
            if (!projection) return;
            const point = projection.fromLatLngToDivPixel(latLng);
            if (point) {
              this.div.style.left = `${point.x}px`;
              this.div.style.top = `${point.y}px`;
            }
          }

          onRemove() {
            if (this.div && this.div.parentNode) {
              this.div.parentNode.removeChild(this.div);
            }
            this.div = null;
          }
        }

        // Inject the pulse animation CSS once
        if (!document.getElementById("campus-pulse-css")) {
          const style = document.createElement("style");
          style.id = "campus-pulse-css";
          style.textContent = `
            @keyframes campusPulse {
              0% { transform: scale(0.6); opacity: 0.8; }
              100% { transform: scale(2.2); opacity: 0; }
            }
          `;
          document.head.appendChild(style);
        }

        const overlay = new CampusOverlay(showSchools ? mapRef.current : null, school, (id: string) => {
          const iw = new g.InfoWindow({
            content: `<div style="font-family:system-ui;min-width:240px;padding:2px">
              <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
                <div style="width:36px;height:36px;border-radius:10px;background:${school.color};display:flex;align-items:center;justify-content:center;color:#fff;font-weight:bold;font-size:16px;flex-shrink:0">${school.logo}</div>
                <div>
                  <div style="font-weight:700;font-size:14px;color:${school.color};line-height:1.2">${school.name}</div>
                  <div style="font-size:10px;color:#888;margin-top:1px">${school.level} · ${school.address}</div>
                </div>
              </div>
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:8px">
                <div style="background:#f5f7fa;border-radius:8px;padding:6px 8px">
                  <div style="font-size:18px;font-weight:700;color:${school.color}">${school.totalStudents.toLocaleString()}</div>
                  <div style="font-size:9px;color:#888;text-transform:uppercase;letter-spacing:0.5px">Total Students</div>
                </div>
                <div style="background:#f5f7fa;border-radius:8px;padding:6px 8px">
                  <div style="font-size:18px;font-weight:700;color:${school.color}">${school.transportStudents.toLocaleString()}</div>
                  <div style="font-size:9px;color:#888;text-transform:uppercase;letter-spacing:0.5px">In Transport</div>
                </div>
                <div style="background:#f5f7fa;border-radius:8px;padding:6px 8px">
                  <div style="font-size:18px;font-weight:700;color:${school.color}">${school.busCount}</div>
                  <div style="font-size:9px;color:#888;text-transform:uppercase;letter-spacing:0.5px">Buses Active</div>
                </div>
                <div style="background:#f5f7fa;border-radius:8px;padding:6px 8px">
                  <div style="font-size:18px;font-weight:700;color:${school.color}">${school.rating}★</div>
                  <div style="font-size:9px;color:#888;text-transform:uppercase;letter-spacing:0.5px">Transport Rating</div>
                </div>
              </div>
              <div style="display:flex;flex-direction:column;gap:3px;font-size:11px;color:#555;margin-bottom:8px">
                <div><span style="font-weight:600">Bell:</span> ${school.bellSchedule}</div>
                <div><span style="font-weight:600">Pickup:</span> ${school.pickupWindow}</div>
                <div><span style="font-weight:600">Drop-off:</span> ${school.dropoffWindow}</div>
                <div><span style="font-weight:600">Manager:</span> ${school.transportManager}</div>
                <div><span style="font-weight:600">Emergency:</span> ${school.emergencyContact}</div>
              </div>
              <div style="margin-bottom:8px">
                <div style="font-size:9px;color:#888;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:3px">Catchment Areas</div>
                <div style="display:flex;flex-wrap:wrap;gap:3px">
                  ${school.catchmentAreas.map((a: string) => `<span style="background:${school.color}15;color:${school.color};border-radius:4px;padding:2px 6px;font-size:9px;font-weight:500">${a}</span>`).join("")}
                </div>
              </div>
              <div style="padding-top:8px;border-top:1px solid #eee;font-size:10px;color:#888;text-align:center">
                Click to view catchment zone & live routes
              </div>
            </div>`,
          });
          iw.open(mapRef.current, marker);
          showSchoolCatchment(id);
        });
        schoolOverlaysRef.current[school.id] = overlay;
        schoolMarkersRef.current[school.id] = marker;
      }
    })();
    return () => {
      cancelled = true;
      Object.values(schoolMarkersRef.current).forEach((m: any) => m?.setMap?.(null));
      Object.values(schoolOverlaysRef.current).forEach((o: any) => o?.setMap?.(null));
      schoolMarkersRef.current = {};
      schoolOverlaysRef.current = {};
    };
  }, [ready, showSchools]);

  // Live open-alert counts per route so the fleet rail shows a red dot next to any bus with unacknowledged alerts
  const { data: alertCounts = {} } = useQuery<Record<string, number>>({
    queryKey: ["bus-alert-counts"],
    refetchInterval: 10_000,
    queryFn: async () => {
      const { data } = await (supabase.from as any)("bus_alerts")
        .select("bus_route,acknowledged_at")
        .is("acknowledged_at", null);
      const counts: Record<string, number> = {};
      (data ?? []).forEach((r: any) => { counts[r.bus_route] = (counts[r.bus_route] ?? 0) + 1; });
      return counts;
    },
  });
  useEffect(() => {
    if (autoTick) return;
    const ch = supabase
      .channel(`bus-alerts-${channelIdRef.current}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "bus_alerts" }, () => qc.invalidateQueries({ queryKey: ["bus-alert-counts"] }))
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [qc, autoTick]);

  const availableRoutes = useMemo(() => Array.from(new Set(positions.map(p => p.route))).sort(), [positions]);

  const filtered = useMemo(
    () => positions.filter(p => {
      const q = search.trim().toLowerCase();
      if (q && !p.route.toLowerCase().includes(q) && !(p.next_stop ?? "").toLowerCase().includes(q)) return false;
      if (routeFilter && p.route !== routeFilter) return false;
      const moving = (p.speed_kmh ?? 0) > 1;
      const openAlerts = alertCounts[p.route] ?? 0;
      if (statusFilter === "moving" && !moving) return false;
      if (statusFilter === "idle" && moving) return false;
      if (statusFilter === "alert" && openAlerts === 0) return false;
      return true;
    }),
    [positions, search, statusFilter, routeFilter, alertCounts]
  );
  const sel = selected ? positions.find(p => p.route === selected) : null;

  const fleetStats = useMemo(() => {
    const moving = positions.filter(p => (p.speed_kmh ?? 0) > 1).length;
    const idle = positions.length - moving;
    const avgSpeed = positions.length ? positions.reduce((a, p) => a + (p.speed_kmh ?? 0), 0) / positions.length : 0;
    return { moving, idle, avgSpeed };
  }, [positions]);

  const detailBus = detailOpen && selected ? positions.find(p => p.route === selected) : null;
  const VehicleModal = (
    <>
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        {detailBus && (
          <DialogContent className="max-w-6xl max-h-[94vh] overflow-y-auto border-0 bg-white p-0 text-slate-900">
            <VehicleDetailModal
              bus={detailBus}
              color={colorFor(detailBus.route)}
              canViewHistory={canViewHistory}
              onOpenHistory={() => { setHistoryBus(detailBus); setDetailOpen(false); }}
              onClose={() => setDetailOpen(false)}
            />
          </DialogContent>
        )}
      </Dialog>
      <RouteHistoryDialog bus={historyBus} onClose={() => setHistoryBus(null)} />
    </>
  );



  // ============================ WIALON LAYOUT ============================
  if (wialon) {
    return (
      <>
      <Card className="overflow-hidden border border-slate-200/60 bg-white text-slate-800 shadow-xl">

        {/* Top bar */}
        <div className="flex flex-col gap-2 border-b border-slate-200/60 bg-slate-50/80 px-3 py-2 backdrop-blur sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span className="grid h-7 w-7 place-items-center rounded-lg bg-emerald-50 text-emerald-600"><Radio className="h-3.5 w-3.5" /></span>
            <div>
              <div className="font-display text-sm font-semibold leading-tight text-slate-800">{title}</div>
              <div className="text-[10px] text-slate-500">Fundred Telematics · GPS feed 5s</div>
            </div>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto text-[11px] text-slate-600 sm:gap-3 sm:overflow-visible">
            <span className="flex shrink-0 items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />{fleetStats.moving} moving</span>
            <span className="flex shrink-0 items-center gap-1 text-amber-600"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" />{fleetStats.idle} idle</span>
            <span className="shrink-0 text-slate-500">Avg {fleetStats.avgSpeed.toFixed(0)} km/h</span>
            <div className="flex shrink-0 items-center gap-1">
              {canViewTrails && (
                <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900" onClick={() => setTrailsOn(v => !v)}>
                  <Layers className="mr-1 h-3 w-3" />Trails {trailsOn ? "on" : "off"}
                </Button>
              )}
              <Button size="sm" variant="ghost" className={`h-7 px-2 hover:bg-slate-100 ${showSchools ? "text-emerald-600 hover:text-emerald-700" : "text-slate-600 hover:text-slate-900"}`} onClick={() => setShowSchools(v => !v)}>
                <School className="mr-1 h-3 w-3" />Schools
              </Button>
              <Button size="sm" variant="ghost" className={`h-7 px-2 hover:bg-slate-100 ${showCatchments ? "text-sky-600 hover:text-sky-700" : "text-slate-600 hover:text-slate-900"}`} onClick={() => { setShowCatchments(v => !v); if (showCatchments) showSchoolCatchment(null); }}>
                <ShieldCheck className="mr-1 h-3 w-3" />Zones
              </Button>
              <Button size="sm" variant="ghost" className={`h-7 px-2 hover:bg-slate-100 ${trafficOn ? "text-amber-600 hover:text-amber-700" : "text-slate-600 hover:text-slate-900"}`} onClick={() => setTrafficOn(v => !v)}>
                <Activity className="mr-1 h-3 w-3" />Traffic
              </Button>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:grid lg:grid-cols-[320px_1fr]" style={{ height }}>
          {/* Left rail · fleet */}
          <div className="order-2 flex max-h-[240px] flex-col border-t border-slate-200/60 bg-slate-50/50 lg:order-1 lg:max-h-none lg:border-r lg:border-t-0">
            <div className="border-b border-slate-200/60 p-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search route, stop…"
                  className="h-8 w-full rounded-lg border border-slate-200 bg-white pl-7 pr-2 text-xs text-slate-700 placeholder:text-slate-400 focus:border-emerald-500/50 focus:outline-none"
                />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1">
                {(["all","moving","idle","alert"] as const).map(s => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider transition ${statusFilter === s ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-500 hover:text-slate-700"}`}
                  >
                    {s === "all" ? "All" : s === "moving" ? "● Moving" : s === "idle" ? "○ Idle" : "! Alerts"}
                  </button>
                ))}
              </div>
              <div className="mt-1.5 flex items-center gap-1">
                <select
                  value={routeFilter ?? ""}
                  onChange={(e) => setRouteFilter(e.target.value || null)}
                  className="h-6 flex-1 rounded-lg border border-slate-200 bg-white px-1.5 text-[10px] text-slate-600 focus:border-emerald-500/50 focus:outline-none"
                >
                  <option value="">All routes / units</option>
                  {availableRoutes.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                {(statusFilter !== "all" || routeFilter || search) && (
                  <button
                    onClick={() => { setStatusFilter("all"); setRouteFilter(null); setSearch(""); }}
                    className="rounded-lg border border-slate-200 bg-white px-1.5 text-[10px] text-slate-500 hover:text-slate-700"
                    title="Clear filters"
                  ><X className="h-3 w-3" /></button>
                )}
              </div>
              <div className="mt-1.5 flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-400">
                <span>Unit · status</span>
                <span>{filtered.length} of {positions.length}</span>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              {filtered.map(p => {
                const t = telemetry(p);
                const isSel = selected === p.route;
                const moving = (p.speed_kmh ?? 0) > 1;
                const openAlerts = alertCounts[p.route] ?? 0;
                return (
                  <button
                    key={p.id}
                    onClick={() => { openDetail(p.route); if (mapRef.current) mapRef.current.panTo({ lat: p.lat, lng: p.lng }); }}

                    className={`grid w-full grid-cols-[10px_1fr_auto] gap-2 border-b border-slate-100 px-3 py-2 text-left text-xs transition hover:bg-slate-100/60 ${isSel ? "bg-emerald-50/60 border-l-2 border-l-emerald-500" : "border-l-2 border-l-transparent"}`}
                  >
                    <span className={`mt-1 h-2 w-2 rounded-full ${moving ? "bg-emerald-500 animate-pulse" : "bg-amber-500"}`} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-sm" style={{ background: colorFor(p.route) }} />
                        <span className="font-medium text-slate-800">{p.route}</span>
                        <span className="font-mono text-[10px] text-slate-400">· {t.plate}</span>
                        {openAlerts > 0 && (
                          <span className="ml-auto inline-flex items-center gap-0.5 rounded-full bg-rose-100 px-1.5 text-[9px] font-semibold text-rose-600 animate-pulse">
                            <TriangleAlert className="h-2.5 w-2.5" />{openAlerts}
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 truncate text-[10px] text-slate-500">{t.driver} · {t.students}/{t.capacity} pax</div>
                      <div className="mt-0.5 truncate text-[10px] text-slate-400">→ {p.next_stop ?? "—"}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm text-slate-800">{Math.round(p.speed_kmh ?? 0)}</div>
                      <div className="text-[9px] uppercase text-slate-400">km/h</div>
                      <div className="mt-1 text-[10px] text-emerald-600">{p.eta_minutes ?? 0}m</div>
                    </div>
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <div className="p-6 text-center text-xs text-slate-400">No units match.</div>
              )}
            </div>
            {/* Selected unit telemetry */}
            {sel && (
              <div className="border-t border-slate-200/60 bg-white p-3">
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Telemetry · {sel.route}</div>
                  <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 text-[9px]">{telemetry(sel).ignition}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <Telem icon={Gauge} label="Speed" v={`${Math.round(sel.speed_kmh ?? 0)} km/h`} />
                  <Telem icon={Navigation} label="Heading" v={`${Math.round(sel.heading ?? 0)}°`} />
                  <Telem icon={Fuel} label="Fuel" v={`${telemetry(sel).fuel}%`} />
                  <Telem icon={Activity} label="Odo" v={`${telemetry(sel).mileage.toLocaleString()} km`} />
                  <Telem icon={User} label="Driver" v={telemetry(sel).driver} />
                  <Telem icon={Clock} label="Last fix" v={new Date(sel.captured_at).toLocaleTimeString()} />
                </div>
                <div className="mt-2 flex gap-1">
                  <Button size="sm" variant="outline" className="h-7 flex-1 border-slate-200 bg-white text-[11px] text-slate-700 hover:bg-slate-50" asChild>
                    <a href={`https://www.google.com/maps/dir/?api=1&destination=${sel.lat},${sel.lng}&travelmode=driving`} target="_blank" rel="noreferrer">
                      <Navigation className="mr-1 h-3 w-3" />Navigate
                    </a>
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 border-slate-200 bg-white text-[11px] text-slate-700 hover:bg-slate-50"
                    onClick={() => { if (mapRef.current) { mapRef.current.panTo({ lat: sel.lat, lng: sel.lng }); mapRef.current.setZoom(16); } }}>
                    <Crosshair className="mr-1 h-3 w-3" />Focus
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Map */}
          <div className="relative order-1 min-h-[300px] lg:order-2 lg:min-h-0">
            <div ref={ref} className="h-full w-full" style={{ background: "#f5f6f8" }} />
            {err && (
              <div className="absolute inset-0 grid place-items-center bg-white/95 p-6 text-center text-sm">
                <div>
                  <AlertCircle className="mx-auto mb-2 h-6 w-6 text-rose-500" />
                  <div className="font-medium text-slate-800">Map unavailable</div>
                  <div className="text-xs text-slate-500">{err}</div>
                </div>
              </div>
            )}
            {!ready && !err && (
              <div className="absolute inset-0 grid place-items-center text-xs text-slate-400">Loading telematics…</div>
            )}
            {/* Recenter on Kampala button */}
            <button
              onClick={() => { if (mapRef.current) { mapRef.current.panTo({ lat: 0.3476, lng: 32.5825 }); mapRef.current.setZoom(12); setSelected(null); } }}
              className="absolute bottom-3 right-3 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-slate-200/60 bg-white/95 text-slate-700 shadow-lg backdrop-blur transition hover:bg-white hover:text-emerald-600"
              title="Recenter on Kampala"
              aria-label="Recenter on Kampala"
            >
              <Crosshair className="h-4 w-4" />
            </button>
            {/* Floating route legend — collapsible on mobile */}
            <div className="absolute right-3 top-3 max-h-[180px] overflow-y-auto rounded-xl border border-slate-200/60 bg-white/90 p-2 text-[10px] shadow-sm backdrop-blur">
              <div className="mb-1 uppercase tracking-wider text-slate-400">Routes</div>
              {Array.from(new Set(positions.map(p => p.route))).map(r => (
                <button key={r} onClick={() => { const bp = positions.find(p => p.route === r); if (bp && mapRef.current) { mapRef.current.panTo({ lat: bp.lat, lng: bp.lng }); mapRef.current.setZoom(15); } }} className="flex items-center gap-1.5 py-0.5 text-slate-600 transition hover:text-slate-900">
                  <span className="h-2 w-2 rounded-sm" style={{ background: colorFor(r) }} />{r}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom ticker */}
        <div className="flex items-center gap-4 overflow-x-auto border-t border-slate-200/60 bg-slate-50/80 px-3 py-1.5 text-[10px] text-slate-500 backdrop-blur">
          <span className="font-mono uppercase text-emerald-600">● LIVE</span>
          {positions.slice(0, 8).map(p => (
            <span key={p.id} className="whitespace-nowrap">
              <span className="text-slate-700">{p.route}</span> · {Math.round(p.speed_kmh ?? 0)} km/h · ETA {p.eta_minutes ?? 0}m · {p.next_stop ?? "—"}
            </span>
          ))}
        </div>
      </Card>
      {VehicleModal}
      </>
    );
  }


  // ============================ DEFAULT LAYOUT ============================
  return (
    <>
    <Card className="overflow-hidden shadow-card">

      <div className="flex items-center justify-between border-b p-3">
        <div className="flex items-center gap-2">
          <span className="relative inline-flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500/70" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
          </span>
          <div className="font-display text-base">{title}</div>
          <Badge variant="secondary" className="ml-1 text-[10px]">{positions.length} live</Badge>
        </div>
        <div className="flex items-center gap-2">
          {positions.map(p => (
            <button key={p.route} onClick={() => { openDetail(p.route); if (mapRef.current) mapRef.current.panTo({ lat: p.lat, lng: p.lng }); }}
              className="flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] hover:bg-accent">
              <span className="h-2 w-2 rounded-full" style={{ background: colorFor(p.route) }} />
              {p.route} · {p.eta_minutes ?? "—"}m
            </button>
          ))}
        </div>
      </div>
      <div className="relative">
        <div ref={ref} style={{ height }} className="w-full bg-muted" />
        {err && (
          <div className="absolute inset-0 grid place-items-center bg-card/95 p-6 text-center text-sm">
            <div>
              <AlertCircle className="mx-auto mb-2 h-6 w-6 text-destructive" />
              <div className="font-medium">Map unavailable</div>
              <div className="text-xs text-muted-foreground">{err}</div>
            </div>
          </div>
        )}
        {!ready && !err && (
          <div className="absolute inset-0 grid place-items-center bg-card/60 text-xs text-muted-foreground">Loading map…</div>
        )}
        {sel && (
          <div className="absolute bottom-3 left-3 right-3 rounded-lg border bg-card/95 p-3 shadow-lg backdrop-blur md:right-auto md:max-w-sm">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className="grid h-7 w-7 place-items-center rounded-full text-white" style={{ background: colorFor(sel.route) }}>
                    <Bus className="h-3.5 w-3.5" />
                  </span>
                  <div className="font-display text-base">{sel.route}</div>
                  <Badge variant="secondary" className="text-[10px]">{Math.round(sel.speed_kmh ?? 0)} km/h</Badge>
                </div>
                <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                  <MapPin className="h-3 w-3" /> Next: {sel.next_stop ?? "—"}
                </div>
                <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">{sel.lat.toFixed(4)}, {sel.lng.toFixed(4)} · {new Date(sel.captured_at).toLocaleTimeString()}</div>
              </div>
              <div className="text-right">
                <div className="font-display text-2xl text-primary leading-none">{sel.eta_minutes ?? 0}<span className="text-xs text-muted-foreground"> min</span></div>
                <div className="text-[9px] uppercase tracking-wider text-muted-foreground">ETA</div>
              </div>
            </div>
            <div className="mt-2 flex gap-1.5">
              <Button size="sm" variant="outline" className="h-7 flex-1 text-xs" asChild>
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${sel.lat},${sel.lng}&travelmode=driving`} target="_blank" rel="noreferrer">
                  <Navigation className="mr-1 h-3 w-3" />Directions
                </a>
              </Button>
              <Button size="sm" variant="outline" className="h-7 flex-1 text-xs" asChild>
                <a href={`https://waze.com/ul?ll=${sel.lat},${sel.lng}&navigate=yes`} target="_blank" rel="noreferrer">
                  <ExternalLink className="mr-1 h-3 w-3" />Waze
                </a>
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setSelected(null)}>Close</Button>
            </div>
          </div>
        )}
        {!sel && positions.length === 0 && ready && (
          <div className="absolute inset-0 grid place-items-center bg-card/40 text-xs text-muted-foreground">No buses currently active.</div>
        )}
      </div>
    </Card>

    {/* School Dashboard Panel */}
    <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
      {SCHOOLS.slice(0, 8).map(school => {
        const schoolBuses = BUSES_BY_SCHOOL[school.id] ?? [];
        const activeBuses = positions.filter(p => {
          const bd = BUSES.find(b => b.vehicleNumber === p.route);
          return bd?.schoolId === school.id;
        });
        const totalStudents = school.transportStudents;
        const picked = Math.min(totalStudents, Math.round(activeBuses.length * 12 + (Date.now() / 1000 % 17)));
        const pct = Math.min(100, (picked / totalStudents) * 100);
        return (
          <div key={school.id} className="group relative overflow-hidden rounded-xl border bg-card p-3 shadow-sm transition hover:shadow-md">
            <div className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-10 transition group-hover:opacity-20" style={{ background: school.color }} />
            <div className="flex items-center gap-2">
              <div className="relative grid h-10 w-10 place-items-center rounded-xl text-sm font-bold text-white shadow-sm" style={{ background: `linear-gradient(135deg, ${school.color}, ${school.color}cc)` }}>
                {school.logo}
                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white" style={{ background: activeBuses.length > 0 ? "#22c55e" : "#94a3b8" }} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-semibold">{school.name}</div>
                <div className="text-[9px] text-muted-foreground">{school.level} · {school.address}</div>
              </div>
            </div>
            <div className="mt-2.5 grid grid-cols-2 gap-1.5 text-[10px]">
              <div className="rounded-lg bg-muted/60 px-2 py-1.5">
                <div className="text-muted-foreground">Buses Active</div>
                <div className="font-bold text-sm" style={{ color: school.color }}>{activeBuses.length}<span className="text-muted-foreground font-normal">/{school.busCount}</span></div>
              </div>
              <div className="rounded-lg bg-muted/60 px-2 py-1.5">
                <div className="text-muted-foreground">Students</div>
                <div className="font-bold text-sm" style={{ color: school.color }}>{picked}<span className="text-muted-foreground font-normal">/{totalStudents}</span></div>
              </div>
              <div className="rounded-lg bg-muted/60 px-2 py-1.5">
                <div className="text-muted-foreground">Rating</div>
                <div className="font-bold text-sm" style={{ color: school.color }}>{school.rating}★</div>
              </div>
              <div className="rounded-lg bg-muted/60 px-2 py-1.5">
                <div className="text-muted-foreground">On-time</div>
                <div className="font-bold text-sm" style={{ color: school.color }}>{school.transportPerformance}%</div>
              </div>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${school.color}, ${school.color}aa)` }} />
            </div>
            <div className="mt-1.5 flex items-center justify-between text-[9px] text-muted-foreground">
              <span>Bell: {school.bellSchedule}</span>
              <span>{schoolBuses.length} routes</span>
            </div>
          </div>
        );
      })}
    </div>

    {VehicleModal}
    </>
  );
}

function Telem({ icon: I, label, v }: { icon: any; label: string; v: string }) {
  return (
    <div className="rounded-md border border-white/10 bg-white/[0.03] p-1.5">
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-slate-500"><I className="h-2.5 w-2.5" />{label}</div>
      <div className="mt-0.5 truncate font-mono text-[11px] text-slate-100">{v}</div>
    </div>
  );
}

/* ============================ MINI LIVE TRACK MAP ============================ */

function MiniTrackMap({ bus, color, focusPoint }: { bus: BusPos; color: string; focusPoint?: { lat: number; lng: number } | null }) {
  const holder = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const trailRef = useRef<any>(null);
  const accuracyRef = useRef<any>(null);
  const trailPtsRef = useRef<{ lat: number; lng: number }[]>([]);
  const [ready, setReady] = useState(false);
  const [follow, setFollow] = useState(true);
  const [mapType, setMapType] = useState<"roadmap" | "hybrid">("roadmap");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadMapsApi();
        if (cancelled || !holder.current) return;
        const g = (window as any).google;
        mapRef.current = new g.maps.Map(holder.current, {
          center: { lat: bus.lat, lng: bus.lng },
          zoom: 16,
          mapTypeId: mapType,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: "greedy",
          clickableIcons: false,
        });
        try { new g.maps.TrafficLayer().setMap(mapRef.current); } catch {}
        markerRef.current = new g.maps.Marker({
          map: mapRef.current,
          position: { lat: bus.lat, lng: bus.lng },
          icon: { url: busSvg(color, bus.heading ?? 0, true), scaledSize: new g.maps.Size(56, 56), anchor: new g.maps.Point(28, 28) },
          zIndex: 999,
        });
        accuracyRef.current = new g.maps.Circle({
          map: mapRef.current, center: { lat: bus.lat, lng: bus.lng }, radius: 30,
          strokeColor: color, strokeOpacity: 0.55, strokeWeight: 1.5,
          fillColor: color, fillOpacity: 0.12,
        });
        trailPtsRef.current = [{ lat: bus.lat, lng: bus.lng }];
        trailRef.current = new g.maps.Polyline({
          map: mapRef.current, path: trailPtsRef.current,
          strokeColor: color, strokeOpacity: 0.9, strokeWeight: 4,
          icons: [{ icon: { path: g.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2.2, strokeColor: color }, offset: "100%", repeat: "80px" }],
        });
        setReady(true);
      } catch {}
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const g = (window as any).google;
    const pos = { lat: bus.lat, lng: bus.lng };
    if (markerRef.current) {
      markerRef.current.setPosition(pos);
      markerRef.current.setIcon({ url: busSvg(color, bus.heading ?? 0, true), scaledSize: new g.maps.Size(56, 56), anchor: new g.maps.Point(28, 28) });
    }
    if (accuracyRef.current) accuracyRef.current.setCenter(pos);
    const last = trailPtsRef.current[trailPtsRef.current.length - 1];
    if (!last || last.lat !== pos.lat || last.lng !== pos.lng) {
      trailPtsRef.current.push(pos);
      if (trailPtsRef.current.length > 120) trailPtsRef.current.shift();
      if (trailRef.current) trailRef.current.setPath(trailPtsRef.current);
    }
    if (follow) mapRef.current.panTo(pos);
  }, [bus.lat, bus.lng, bus.heading, color, ready, follow]);

  useEffect(() => { if (mapRef.current) mapRef.current.setMapTypeId(mapType); }, [mapType]);

  // External focus (from timeline click) — pan without disabling live follow permanently
  useEffect(() => {
    if (!ready || !mapRef.current || !focusPoint) return;
    mapRef.current.panTo(focusPoint);
    mapRef.current.setZoom(17);
    setFollow(false);
  }, [focusPoint, ready]);

  return (
    <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-slate-100 shadow-sm">
      <div ref={holder} className="h-[320px] w-full" />
      {!ready && (
        <div className="pointer-events-none absolute inset-0 grid place-items-center text-xs text-slate-500">Loading live map…</div>
      )}
      <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between gap-2 p-2">
        <div className="pointer-events-auto flex items-center gap-1.5 rounded-full bg-white/95 px-2.5 py-1 shadow-sm ring-1 ring-slate-200">
          <CircleDot className="h-2.5 w-2.5 animate-pulse text-rose-500" />
          <span className="text-[10px] font-medium uppercase tracking-wider text-slate-700">Live · {bus.route}</span>
        </div>
        <div className="pointer-events-auto flex items-center gap-1">
          <Button size="sm" variant="secondary" className={`h-7 px-2 text-[10px] shadow-sm ${follow ? "bg-slate-900 text-white hover:bg-slate-800" : "bg-white/95 text-slate-700 hover:bg-white"}`} onClick={() => setFollow(v => !v)}>
            <Crosshair className="mr-1 h-3 w-3" />{follow ? "Following" : "Follow"}
          </Button>
          <Button size="sm" variant="secondary" className="h-7 bg-white/95 px-2 text-[10px] text-slate-700 shadow-sm hover:bg-white" onClick={() => setMapType(t => t === "roadmap" ? "hybrid" : "roadmap")}>
            <Layers className="mr-1 h-3 w-3" />{mapType === "roadmap" ? "Satellite" : "Road"}
          </Button>
        </div>
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-gradient-to-t from-white/95 to-transparent p-2 text-[10px] text-slate-600">
        <span className="font-mono">{bus.lat.toFixed(5)}, {bus.lng.toFixed(5)} · {Math.round(bus.heading ?? 0)}°</span>
        <span className="flex items-center gap-1"><Activity className="h-3 w-3" />{Math.round(bus.speed_kmh ?? 0)} km/h · ETA {bus.eta_minutes ?? 0}m → {bus.next_stop ?? "—"}</span>
      </div>
    </div>
  );
}

/* ============================ VEHICLE DETAIL MODAL ============================ */

function VehicleDetailModal({ bus: initialBus, color, canViewHistory, onOpenHistory, onClose }: { bus: BusPos; color: string; canViewHistory?: boolean; onOpenHistory?: () => void; onClose: () => void }) {
  const qc = useQueryClient();
  const channelIdRef = useRef(Math.random().toString(36).slice(2, 10));
  // Live overlay: subscribe to position updates for this exact bus so the modal
  // stays in near real-time even between parent poll cycles.
  const [livePos, setLivePos] = useState<BusPos>(initialBus);
  useEffect(() => { setLivePos(initialBus); }, [initialBus.id, initialBus.captured_at]);
  useEffect(() => {
    const ch = supabase
      .channel(`bus-modal-${channelIdRef.current}-${initialBus.id}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "bus_positions", filter: `id=eq.${initialBus.id}` },
        (payload: any) => { if (payload.new) setLivePos(payload.new as BusPos); }
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [initialBus.id]);
  const bus = livePos;

  const t = telemetry(bus);
  const speed = Math.round(bus.speed_kmh ?? 0);
  const moving = speed > 1;
  const occupancyPct = Math.round((t.students / t.capacity) * 100);
  const fuelPct = t.fuel;
  const [now, setNow] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(id); }, []);

  // Click a timeline row → pan the mini map
  const [focusPoint, setFocusPoint] = useState<{ lat: number; lng: number } | null>(null);

  // Reverse geocode the exact street using rounded coords so we don't refetch on every micro-move
  const lat4 = Math.round(bus.lat * 1000) / 1000;
  const lng4 = Math.round(bus.lng * 1000) / 1000;
  const { data: addr } = useQuery({
    queryKey: ["geo", lat4, lng4],
    staleTime: 60_000,
    queryFn: async () => {
      const { reverseGeocode } = await import("@/lib/geocode.functions");
      return await reverseGeocode({ data: { lat: bus.lat, lng: bus.lng } });
    },
  });

  const routeStopDefs = ROUTE_STOPS[bus.route] ?? ROUTE_STOPS[Object.keys(ROUTE_STOPS)[0]] ?? [];
  const { stops, routeProgressPct } = useMemo(() => {
    let nearestIdx = 0;
    let nearestDist = Infinity;
    for (let i = 0; i < routeStopDefs.length; i++) {
      const dLat = bus.lat - routeStopDefs[i].lat;
      const dLng = bus.lng - routeStopDefs[i].lng;
      const d = dLat * dLat + dLng * dLng;
      if (d < nearestDist) { nearestDist = d; nearestIdx = i; }
    }
    // Fractional progress between nearest stop and the next
    let frac = 0;
    if (nearestIdx < routeStopDefs.length - 1) {
      const prev = routeStopDefs[nearestIdx];
      const next = routeStopDefs[nearestIdx + 1];
      const segLen = Math.sqrt((next.lat - prev.lat) ** 2 + (next.lng - prev.lng) ** 2);
      const distFromPrev = Math.sqrt((bus.lat - prev.lat) ** 2 + (bus.lng - prev.lng) ** 2);
      frac = segLen > 0 ? Math.min(1, distFromPrev / segLen) : 0;
    }
    const pct = Math.min(100, ((nearestIdx + frac) / Math.max(1, routeStopDefs.length - 1)) * 100);
    const arr = routeStopDefs.map((s, i) => ({
      label: s.label, idx: i,
      done: i < nearestIdx,
      current: i === nearestIdx,
    }));
    return { stops: arr, routeProgressPct: pct };
  }, [bus.route, bus.lat, bus.lng, routeStopDefs]);

  // ─── Live alerts feed (geofence, deviation, over-speed, dwell) ──────────
  const alertsQuery = useQuery({
    queryKey: ["bus-alerts", bus.route],
    refetchInterval: 8000,
    queryFn: async () => {
      const { data } = await (supabase.from as any)("bus_alerts")
        .select("*")
        .eq("bus_route", bus.route)
        .order("created_at", { ascending: false })
        .limit(15);
      return (data ?? []) as any[];
    },
  });
  useEffect(() => {
    const ch = supabase
      .channel(`bus-alerts-${channelIdRef.current}-${bus.route}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "bus_alerts", filter: `bus_route=eq.${bus.route}` },
        () => qc.invalidateQueries({ queryKey: ["bus-alerts", bus.route] })
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [bus.route, qc]);
  const liveAlerts = alertsQuery.data ?? [];

  const acknowledgeAlert = async (alertId: string) => {
    const { error } = await (supabase as any).rpc("acknowledge_bus_alert", { _alert_id: alertId, _note: null });
    if (error) { toast.error(error.message ?? "Could not acknowledge"); return; }
    toast.success("Alert acknowledged · audit logged");
    qc.invalidateQueries({ queryKey: ["bus-alerts", bus.route] });
  };



  const cams = [
    { id: "CAM-01", label: "Front", recording: true,  hue: 200, tag: "FWD" },
    { id: "CAM-02", label: "Cabin", recording: true,  hue: 30,  tag: "INT" },
    { id: "CAM-03", label: "Rear",  recording: true,  hue: 0,   tag: "REV" },
    { id: "CAM-04", label: "Door",  recording: false, hue: 140, tag: "DR" },
  ];

  return (
    <div className="relative bg-white text-slate-900">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-40" style={{ background: `radial-gradient(120% 100% at 10% 0%, ${color}22 0%, transparent 60%)` }} />

      <div className="relative flex items-start justify-between gap-4 border-b border-slate-200 p-5">
        <div className="flex items-center gap-3">
          <div className="relative grid h-14 w-14 place-items-center rounded-2xl shadow-lg" style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}>
            <Bus className="h-6 w-6 text-white" />
            <span className={`absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full border-2 border-white ${moving ? "bg-emerald-500" : "bg-amber-500"}`}>
              <span className={`h-1.5 w-1.5 rounded-full bg-white ${moving ? "animate-pulse" : ""}`} />
            </span>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-display text-2xl tracking-tight text-slate-900">{bus.route}</h2>
              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-emerald-200 text-[10px]">{moving ? "MOVING" : "IDLE"}</Badge>
              <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600 text-[10px]"><Wifi className="mr-1 h-2.5 w-2.5" />4G · 92%</Badge>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
              <span className="font-mono text-slate-700">{t.plate}</span>
              <span>·</span>
              <span>{t.fleetNumber}</span>
              <span>·</span>
              <span className="font-medium" style={{ color }}>{t.schoolName}</span>
              <span>·</span>
              <span>{t.routeName}</span>
              <span>·</span>
              <span className="flex items-center gap-1"><Disc className="h-3 w-3 text-rose-500 animate-pulse" /> 4 cameras recording</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-right">
            <div className="font-mono text-xs text-slate-700">{now.toLocaleTimeString()}</div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500">Onboard clock · EAT</div>
          </div>
          <Button size="icon" variant="ghost" className="text-slate-500 hover:bg-slate-100 hover:text-slate-900" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Exact location strip — reverse geocoded */}
      <div className="relative grid gap-3 border-b border-slate-200 bg-gradient-to-br from-slate-50 to-white px-5 py-3 sm:grid-cols-[1fr_auto]">
        <div className="flex items-start gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-lg bg-emerald-50 text-emerald-700">
            <MapPin className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Exact location · live</div>
            <div className="truncate text-sm font-semibold text-slate-900">
              {addr?.formatted ?? `${bus.next_stop ?? "Locating"} — geocoding…`}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-[10px] text-slate-500">
              <span>{bus.lat.toFixed(5)}, {bus.lng.toFixed(5)}</span>
              <span>·</span>
              <span>{addr?.neighborhood ?? "—"}{addr?.locality ? ` · ${addr.locality}` : ""}</span>
              <span>·</span>
              <span>heading {Math.round(bus.heading ?? 0)}°</span>
              <span>·</span>
              <span>fix {Math.max(1, Math.floor((Date.now() - new Date(bus.captured_at).getTime()) / 1000))}s ago</span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" variant="outline" className="h-8 text-xs" asChild>
            <a href={`https://www.google.com/maps?q=${bus.lat},${bus.lng}`} target="_blank" rel="noreferrer">
              <ExternalLink className="mr-1 h-3 w-3" />Open in Maps
            </a>
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" asChild>
            <a href={`https://www.google.com/maps/dir/?api=1&destination=${bus.lat},${bus.lng}&travelmode=driving`} target="_blank" rel="noreferrer">
              <Navigation className="mr-1 h-3 w-3" />Navigate to bus
            </a>
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs"
            onClick={() => { navigator.clipboard.writeText(`${bus.lat},${bus.lng}`); toast.success("Coordinates copied"); }}>
            <Crosshair className="mr-1 h-3 w-3" />Copy GPS
          </Button>
          {canViewHistory && (
            <Button size="sm" className="h-8 bg-slate-900 text-xs text-white hover:bg-slate-800" onClick={onOpenHistory}>
              <HistoryIcon className="mr-1 h-3 w-3" />Route history
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-4 p-5 lg:grid-cols-[1.3fr_1fr]">

        <div className="space-y-4">
          <MiniTrackMap bus={bus} color={color} focusPoint={focusPoint} />
          <div>
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500"><Video className="h-3 w-3" />Live cabin & exterior CCTV</div>
              <div className="flex items-center gap-1.5 text-[10px] text-slate-500"><CircleDot className="h-3 w-3 text-rose-500 animate-pulse" />REC · 1080p · {now.toLocaleTimeString()}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {cams.map(c => <CamTile key={c.id} cam={c} routeLabel={bus.route} now={now} />)}
            </div>
            <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500">
              <span className="flex items-center gap-1.5"><Lock className="h-3 w-3" />Encrypted feed · school admin only</span>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-600 hover:bg-slate-100"><Volume2 className="mr-1 h-3 w-3" />Listen</Button>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-600 hover:bg-slate-100"><Maximize2 className="mr-1 h-3 w-3" />Fullscreen</Button>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500"><RouteIcon className="h-3 w-3" />Route progress</div>
              <Badge variant="outline" className="border-slate-200 bg-white text-slate-700 text-[10px]">ETA {bus.eta_minutes ?? 0} min · {bus.next_stop ?? "—"}</Badge>
            </div>
            <div className="relative">
              <div className="absolute left-0 right-0 top-3 h-0.5 bg-slate-200" />
              <div className="absolute left-0 top-3 h-0.5 transition-all duration-700" style={{ width: `${routeProgressPct}%`, background: color }} />
              <div className="relative flex justify-between">
                {stops.map(s => (
                  <div key={s.idx} className="flex flex-col items-center gap-1.5" style={{ width: 80 }}>
                    <span className={`grid h-6 w-6 place-items-center rounded-full border-2 ${s.done ? "border-emerald-500 bg-emerald-50 text-emerald-700" : s.current ? "border-white text-white shadow-md" : "border-slate-200 bg-white text-slate-400"}`} style={s.current ? { background: color } : undefined}>
                      <span className="text-[9px] font-mono">{s.idx + 1}</span>
                    </span>
                    <span className={`text-center text-[9px] leading-tight ${s.current ? "font-medium text-slate-900" : s.done ? "text-emerald-700" : "text-slate-500"}`}>{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <ManifestPanel bus={bus} students={t.students} capacity={t.capacity} color={color} />
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <RadialGauge label="Speed" value={speed} max={80} unit="km/h" color={color} sub={`${Math.round(bus.heading ?? 0)}° heading`} />
            <RadialGauge label="Occupancy" value={t.students} max={t.capacity} unit={`/ ${t.capacity}`} color="#10b981" sub={`${occupancyPct}% full`} />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Vital icon={Fuel} label="Fuel" value={`${fuelPct}%`} tone={fuelPct < 25 ? "warn" : "ok"} bar={fuelPct} />
            <Vital icon={BatteryFull} label="Battery" value="13.6 V" tone="ok" bar={88} />
            <Vital icon={Thermometer} label="Engine" value="82°C" tone="ok" bar={64} />
            <Vital icon={Snowflake} label="Cabin AC" value="23°C" tone="ok" bar={45} />
            <Vital icon={Activity} label="Odometer" value={`${t.mileage.toLocaleString()} km`} bar={0} flat />
            <Vital icon={Gauge} label="Engine RPM" value={moving ? "1,820" : "780"} bar={moving ? 60 : 20} />
          </div>

          <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4">
            <div className="mb-2 flex items-center justify-between text-[11px] uppercase tracking-wider text-slate-500">
              <span className="flex items-center gap-2"><User className="h-3 w-3" />Driver on duty</span>
              <span className="flex items-center gap-1 text-emerald-700 text-[10px]"><ShieldCheck className="h-3 w-3" />Licensed · ID #DR-{(hash(bus.id) % 9000 + 1000)}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="grid h-12 w-12 place-items-center rounded-full text-base font-display text-white shadow-md" style={{ background: `linear-gradient(135deg, ${color}, ${color}99)` }}>
                {t.driver.split(" ").map(p => p[0]).slice(0, 2).join("")}
              </span>
              <div className="flex-1">
                <div className="font-medium text-slate-900">{t.driver}</div>
                <div className="text-[11px] text-slate-500">Driver · 4yr w/ school · safety 98 · 0 incidents</div>
                <div className="text-[11px] text-slate-500">Conductor: {t.conductor} · {t.schoolName}</div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-8" onClick={() => toast.success(`Calling ${t.driver}…`)}>
                  <Phone className="mr-1 h-3 w-3" />Call
                </Button>
                <Button size="sm" variant="outline" className="h-8" onClick={() => toast.success("PTT channel open · speak now")}>
                  <Mic className="mr-1 h-3 w-3" />PTT
                </Button>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500">
                <TriangleAlert className="h-3 w-3" />Geofence & deviation alerts
              </div>
              <Badge variant="outline" className="border-slate-200 bg-slate-50 text-[10px] text-slate-600">
                {liveAlerts.filter(a => !a.acknowledged_at).length} open · {liveAlerts.length} total
              </Badge>
            </div>
            <ul className="max-h-56 space-y-1.5 overflow-y-auto pr-1 text-[11px]">
              {liveAlerts.length === 0 && (
                <li className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-center text-[11px] text-slate-500">
                  No alerts. Bus operating within geofence & route corridor.
                </li>
              )}
              {liveAlerts.map((a) => {
                const ackd = !!a.acknowledged_at;
                const toneCls = a.severity === "critical"
                  ? { border: "border-rose-200", bg: "bg-rose-50", dot: "bg-rose-500", chip: "bg-rose-100 text-rose-700" }
                  : a.severity === "warn"
                  ? { border: "border-amber-200", bg: "bg-amber-50", dot: "bg-amber-500", chip: "bg-amber-100 text-amber-700" }
                  : { border: "border-sky-200", bg: "bg-sky-50", dot: "bg-sky-500", chip: "bg-sky-100 text-sky-700" };
                return (
                  <li key={a.id} className={`group flex items-start gap-2 rounded-md border px-2.5 py-1.5 ${ackd ? "border-slate-200 bg-slate-50/60 opacity-70" : `${toneCls.border} ${toneCls.bg}`}`}>
                    <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${toneCls.dot} ${ackd ? "" : "animate-pulse"}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-[9px] uppercase tracking-wider text-slate-500">{a.alert_type.replace("_", " ")}</span>
                        <span className={`rounded px-1 text-[9px] uppercase ${toneCls.chip}`}>{a.severity}</span>
                        <span className="ml-auto text-[10px] text-slate-500">{new Date(a.created_at).toLocaleTimeString()}</span>
                      </div>
                      <div className="mt-0.5 truncate text-slate-800">{a.message}</div>
                      {a.lat && a.lng && (
                        <button
                          onClick={() => setFocusPoint({ lat: a.lat, lng: a.lng })}
                          className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-900"
                        >
                          <Crosshair className="h-2.5 w-2.5" />
                          {a.lat.toFixed(4)}, {a.lng.toFixed(4)}
                        </button>
                      )}
                      {ackd && (
                        <div className="mt-0.5 text-[9px] italic text-emerald-700">
                          Acknowledged {new Date(a.acknowledged_at).toLocaleTimeString()}
                          {a.acknowledgement_note ? ` · ${a.acknowledgement_note}` : ""}
                        </div>
                      )}
                    </div>
                    {!ackd && (
                      <Button
                        size="sm" variant="outline"
                        className="h-6 shrink-0 px-2 text-[10px]"
                        onClick={() => acknowledgeAlert(a.id)}
                      >
                        Ack
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          <RouteTimelinePanel busId={bus.id} route={bus.route} color={color} onFocus={setFocusPoint} />

        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
        <span className="text-[10px] uppercase tracking-wider text-slate-500">Quick actions</span>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="h-8" asChild>
            <a href={`https://www.google.com/maps/dir/?api=1&destination=${bus.lat},${bus.lng}`} target="_blank" rel="noreferrer"><Navigation className="mr-1 h-3 w-3" />Navigate to bus</a>
          </Button>
          <Button size="sm" variant="outline" className="h-8" onClick={() => toast.success("Broadcast sent to cabin speakers")}><Megaphone className="mr-1 h-3 w-3" />Broadcast cabin</Button>
          <Button size="sm" variant="outline" className="h-8" onClick={() => toast.success(`Snapshot saved · ${bus.route} @ ${now.toLocaleTimeString()}`)}><Camera className="mr-1 h-3 w-3" />Snapshot</Button>
          <Button size="sm" className="h-8 bg-rose-600 text-white hover:bg-rose-700" onClick={() => toast.error("SOS dispatched · 999 + school admin notified")}><TriangleAlert className="mr-1 h-3 w-3" />SOS</Button>
        </div>
      </div>
    </div>
  );
}

function ManifestPanel({ bus, students, capacity, color }: { bus: BusPos; students: number; capacity: number; color: string }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<"all" | "onboard" | "expected" | "missed">("all");
  const [openId, setOpenId] = useState<string | null>(null);

  const roster = useMemo(() => {
    const firsts = ["Nakato", "Mukasa", "Acan", "Otim", "Namugga", "Lubega", "Atim", "Ssali", "Auma", "Kato", "Kirabo", "Mwesigwa", "Nansubuga", "Okello", "Nabirye", "Tumwine", "Aine", "Babirye", "Kiprotich", "Asiimwe", "Kasumba", "Nankya", "Wasswa", "Birungi", "Mugisha", "Nalubega", "Opio", "Achieng"];
    const lasts = ["A.", "B.", "K.", "M.", "N.", "S.", "T.", "O."];
    const schools = ["Greenhill Academy", "St Mary's Kisubi", "Kampala Parents", "Buganda Road PS", "Mengo SS"];
    const classes = ["P3", "P4", "P5", "P6", "P7", "S1", "S2", "S3"];
    const stops = ["Naalya Stage", "Ntinda Junction", "Kamwokya", "Bukoto", "Kisaasi", "Kira Rd", "Kololo"];
    const total = capacity;
    const onboardN = students;
    return Array.from({ length: total }).map((_, i) => {
      const seed = hash(bus.id + ":" + i);
      const isBoarded = i < onboardN;
      const status: "onboard" | "expected" | "missed" = isBoarded ? "onboard" : (seed % 7 === 0 ? "missed" : "expected");
      const min = 35 + (seed % 35);
      const tapTime = `06:${min.toString().padStart(2, "0")}`;
      return {
        id: `STU-${seed % 9000 + 1000}`,
        seat: ((i % 26) + 1).toString(),
        name: `${firsts[seed % firsts.length]} ${lasts[(seed >> 3) % lasts.length]}`,
        initials: firsts[seed % firsts.length].slice(0, 1) + lasts[(seed >> 3) % lasts.length].slice(0, 1),
        school: schools[seed % schools.length],
        cls: classes[(seed >> 2) % classes.length],
        stop: stops[(seed >> 4) % stops.length],
        tapTime,
        status,
        temp: 36.2 + ((seed % 14) / 10),
        parent: `+256 7${(seed % 9 + 1)}${(seed % 9000 + 1000)} ${(seed % 900 + 100)}`,
        guardian: ["Mum", "Dad", "Auntie", "Uncle"][seed % 4],
        notes: seed % 11 === 0 ? "Allergic to peanuts" : seed % 9 === 0 ? "Asthma inhaler in bag" : seed % 13 === 0 ? "Drop at gate · no walk" : "",
        feePaid: seed % 8 !== 0,
        rfid: `NFC-${(seed % 90000 + 10000).toString(16).toUpperCase()}`,
        color: `hsl(${(seed * 47) % 360} 60% 50%)`,
      };
    });
  }, [bus.id, students, capacity]);

  const counts = useMemo(() => ({
    all: roster.length,
    onboard: roster.filter(r => r.status === "onboard").length,
    expected: roster.filter(r => r.status === "expected").length,
    missed: roster.filter(r => r.status === "missed").length,
  }), [roster]);

  const filtered = roster.filter(r => {
    if (filter !== "all" && r.status !== filter) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return r.name.toLowerCase().includes(s) || r.school.toLowerCase().includes(s) || r.id.includes(q) || r.stop.toLowerCase().includes(s) || r.cls.toLowerCase().includes(s);
  });

  const open = openId ? roster.find(r => r.id === openId) : null;
  const flagged = roster.filter(r => r.notes).length;
  const unpaid = roster.filter(r => !r.feePaid).length;

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 p-3">
        <div className="flex items-center gap-2">
          <div className="grid h-8 w-8 place-items-center rounded-lg" style={{ background: `${color}15`, color }}>
            <Users className="h-4 w-4" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900">Live manifest</div>
            <div className="text-[11px] text-slate-500">{counts.onboard}/{capacity} onboard · {counts.expected} expected · {counts.missed} missed · {flagged} flagged</div>
          </div>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => toast.success("Roll-call broadcast sent to driver tablet")}><Megaphone className="mr-1 h-3 w-3" />Roll-call</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => toast.success("Manifest CSV downloaded")}><ChevronRight className="mr-1 h-3 w-3 rotate-90" />Export</Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-0 border-b border-slate-200 text-center">
        {([
          { k: "all", l: "All", v: counts.all, tone: "slate" },
          { k: "onboard", l: "Onboard", v: counts.onboard, tone: "emerald" },
          { k: "expected", l: "Expected", v: counts.expected, tone: "amber" },
          { k: "missed", l: "Missed", v: counts.missed, tone: "rose" },
        ] as const).map(t => (
          <button key={t.k} onClick={() => setFilter(t.k)}
            className={`border-r border-slate-200 px-2 py-2 text-left transition-colors last:border-r-0 ${filter === t.k ? "bg-slate-50" : "hover:bg-slate-50/60"}`}>
            <div className={`text-[10px] uppercase tracking-wider ${filter === t.k ? "text-slate-900 font-semibold" : "text-slate-500"}`}>{t.l}</div>
            <div className={`font-display text-lg ${t.tone === "emerald" ? "text-emerald-600" : t.tone === "amber" ? "text-amber-600" : t.tone === "rose" ? "text-rose-600" : "text-slate-900"}`}>{t.v}</div>
          </button>
        ))}
      </div>

      <div className="border-b border-slate-200 p-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search name, school, class, stop, ID…"
            className="w-full rounded-md border border-slate-200 bg-white py-1.5 pl-8 pr-2 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
          />
        </div>
      </div>

      <div className="max-h-[360px] divide-y divide-slate-100 overflow-auto">
        {filtered.length === 0 && (
          <div className="p-6 text-center text-xs text-slate-500">No students match.</div>
        )}
        {filtered.map(r => (
          <button key={r.id} onClick={() => setOpenId(r.id)}
            className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-slate-50">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-[11px] font-mono font-semibold text-white shadow-sm" style={{ background: r.color }}>
              {r.initials}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 truncate">
                <span className="truncate text-sm font-medium text-slate-900">{r.name}</span>
                <span className="rounded bg-slate-100 px-1 text-[9px] font-mono text-slate-600">{r.cls}</span>
                {r.notes && <TriangleAlert className="h-3 w-3 text-amber-500" />}
                {!r.feePaid && <Lock className="h-3 w-3 text-rose-500" />}
              </div>
              <div className="flex items-center gap-1 truncate text-[10px] text-slate-500">
                <MapPin className="h-2.5 w-2.5" /><span className="truncate">{r.stop}</span>
                <span>·</span>
                <span className="truncate">{r.school}</span>
              </div>
            </div>
            <div className="shrink-0 text-right">
              {r.status === "onboard" && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-medium text-emerald-700"><CircleDot className="h-2 w-2" />Tap {r.tapTime}</span>}
              {r.status === "expected" && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[9px] font-medium text-amber-700"><Clock className="h-2 w-2" />Expected</span>}
              {r.status === "missed" && <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-1.5 py-0.5 text-[9px] font-medium text-rose-700"><X className="h-2 w-2" />No-show</span>}
              <div className="mt-0.5 font-mono text-[9px] text-slate-400">Seat {r.seat}</div>
            </div>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-slate-200 bg-slate-50/60 p-2 text-[10px] text-slate-500">
        <span>{unpaid} unpaid fees · {flagged} medical flags</span>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => toast.success(`Alerted ${counts.missed} parents of no-shows`)}>Alert no-shows</Button>
          <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => toast.success("School office notified · manifest synced")}>Sync to school</Button>
        </div>
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent className="max-w-md bg-white p-0">
          {open && (
            <div>
              <div className="flex items-center gap-3 border-b border-slate-200 p-4" style={{ background: `linear-gradient(135deg, ${open.color}18, transparent)` }}>
                <span className="grid h-14 w-14 place-items-center rounded-2xl text-base font-display font-bold text-white shadow-md" style={{ background: open.color }}>
                  {open.initials}
                </span>
                <div className="flex-1">
                  <div className="text-base font-semibold text-slate-900">{open.name}</div>
                  <div className="text-xs text-slate-500">{open.school} · {open.cls} · ID {open.id}</div>
                </div>
                {open.status === "onboard" && <Badge className="bg-emerald-600">Onboard</Badge>}
                {open.status === "expected" && <Badge variant="outline" className="border-amber-300 text-amber-700">Expected</Badge>}
                {open.status === "missed" && <Badge variant="destructive">No-show</Badge>}
              </div>
              <div className="grid grid-cols-2 gap-px bg-slate-100 text-xs">
                <div className="bg-white p-3"><div className="text-[10px] uppercase text-slate-500">Pickup stop</div><div className="font-medium text-slate-900">{open.stop}</div></div>
                <div className="bg-white p-3"><div className="text-[10px] uppercase text-slate-500">Boarded</div><div className="font-medium text-slate-900">{open.status === "onboard" ? open.tapTime : "—"}</div></div>
                <div className="bg-white p-3"><div className="text-[10px] uppercase text-slate-500">Seat</div><div className="font-mono font-medium text-slate-900">{open.seat}</div></div>
                <div className="bg-white p-3"><div className="text-[10px] uppercase text-slate-500">NFC tag</div><div className="font-mono text-[11px] text-slate-700">{open.rfid}</div></div>
                <div className="bg-white p-3"><div className="text-[10px] uppercase text-slate-500">Temp at boarding</div><div className={`font-medium ${open.temp > 37.5 ? "text-rose-600" : "text-slate-900"}`}>{open.temp.toFixed(1)}°C</div></div>
                <div className="bg-white p-3"><div className="text-[10px] uppercase text-slate-500">Fees</div><div className={`font-medium ${open.feePaid ? "text-emerald-600" : "text-rose-600"}`}>{open.feePaid ? "Up to date" : "Outstanding"}</div></div>
              </div>
              <div className="space-y-2 border-t border-slate-200 p-4">
                <div className="rounded-lg border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Guardian on file ({open.guardian})</div>
                  <div className="mt-1 flex items-center justify-between">
                    <div className="font-mono text-sm text-slate-900">{open.parent}</div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => toast.success(`Calling ${open.guardian}…`)}><Phone className="mr-1 h-3 w-3" />Call</Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => toast.success("SMS sent to guardian")}><Radio className="mr-1 h-3 w-3" />SMS</Button>
                    </div>
                  </div>
                </div>
                {open.notes && (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <TriangleAlert className="mt-0.5 h-4 w-4 text-amber-600" />
                    <div><div className="text-[10px] uppercase tracking-wider text-amber-700">Medical / dispatch note</div><div className="text-sm text-amber-900">{open.notes}</div></div>
                  </div>
                )}
                <div className="flex gap-2 pt-1">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => toast.success(`Manual check-in recorded for ${open.name}`)}>Manual check-in</Button>
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => toast.success(`Marked ${open.name} as absent today`)}>Mark absent</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ============================ ROUTE TIMELINE PANEL ============================ */

type HistRow = { id: number; lat: number; lng: number; speed_kmh: number | null; next_stop: string | null; captured_at: string };

function RouteTimelinePanel({ busId, route, color, onFocus }: {
  busId: string;
  route: string;
  color: string;
  onFocus: (p: { lat: number; lng: number }) => void;
}) {
  const qc = useQueryClient();
  const channelIdRef = useRef(Math.random().toString(36).slice(2, 10));
  const { data: rows = [] } = useQuery<HistRow[]>({
    queryKey: ["bus-timeline", busId],
    refetchInterval: 6000,
    queryFn: async () => {
      const { data } = await (supabase.from as any)("bus_position_history")
        .select("id,lat,lng,speed_kmh,next_stop,captured_at")
        .eq("bus_id", busId)
        .order("captured_at", { ascending: false })
        .limit(30);
      return (data ?? []) as HistRow[];
    },
  });
  // Live tail on new history snapshots
  useEffect(() => {
    const ch = supabase
      .channel(`bus-hist-${channelIdRef.current}-${busId}`)
      .on("postgres_changes",
        { event: "INSERT", schema: "public", table: "bus_position_history", filter: `bus_id=eq.${busId}` },
        () => qc.invalidateQueries({ queryKey: ["bus-timeline", busId] })
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [busId, qc]);

  // Compute dwell per row = seconds since previous fix (rows are DESC, so previous = next in array)
  const enriched = useMemo(() => {
    return rows.map((r, i) => {
      const prev = rows[i + 1];
      const dwellSec = prev ? Math.max(0, Math.floor((new Date(r.captured_at).getTime() - new Date(prev.captured_at).getTime()) / 1000)) : 0;
      const stopped = (r.speed_kmh ?? 0) < 1.5;
      return { ...r, dwellSec, stopped };
    });
  }, [rows]);

  const stats = useMemo(() => {
    if (!enriched.length) return { avgSpeed: 0, maxSpeed: 0, stopped: 0 };
    const speeds = enriched.map(r => r.speed_kmh ?? 0);
    return {
      avgSpeed: speeds.reduce((a, b) => a + b, 0) / speeds.length,
      maxSpeed: Math.max(...speeds),
      stopped: enriched.filter(r => r.stopped).length,
    };
  }, [enriched]);

  const fmtDwell = (s: number) => s < 60 ? `${s}s` : s < 3600 ? `${Math.floor(s/60)}m ${s%60}s` : `${Math.floor(s/3600)}h ${Math.floor((s%3600)/60)}m`;

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500">
          <HistoryIcon className="h-3 w-3" />Route timeline · last {enriched.length} fixes
        </div>
        <div className="flex items-center gap-3 text-[10px] text-slate-500">
          <span>avg <b className="font-mono text-slate-800">{stats.avgSpeed.toFixed(0)}</b> km/h</span>
          <span>peak <b className="font-mono text-slate-800">{stats.maxSpeed.toFixed(0)}</b></span>
          <span><b className="font-mono text-slate-800">{stats.stopped}</b> stops</span>
        </div>
      </div>
      <div className="max-h-72 overflow-y-auto">
        {enriched.length === 0 && (
          <div className="p-4 text-center text-[11px] text-slate-500">No telematics history yet for {route}.</div>
        )}
        <ol className="relative">
          {enriched.map((r, i) => {
            const isNow = i === 0;
            const spd = Math.round(r.speed_kmh ?? 0);
            return (
              <li key={r.id} className="relative">
                <button
                  onClick={() => onFocus({ lat: r.lat, lng: r.lng })}
                  className="grid w-full grid-cols-[24px_60px_1fr_auto] items-center gap-2 border-b border-slate-100 px-3 py-2 text-left transition hover:bg-slate-50"
                >
                  <div className="relative flex h-full items-center justify-center">
                    {i < enriched.length - 1 && <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-slate-200" />}
                    <span
                      className={`relative z-10 h-2.5 w-2.5 rounded-full ${isNow ? "ring-2 ring-white" : ""} ${r.stopped ? "bg-amber-500" : "bg-emerald-500"} ${isNow ? "animate-pulse" : ""}`}
                      style={isNow ? { background: color } : undefined}
                    />
                  </div>
                  <div className="font-mono text-[10px] text-slate-500">
                    {new Date(r.captured_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-[11px] text-slate-800">
                      {r.next_stop ? <>→ {r.next_stop}</> : <span className="font-mono text-slate-500">{r.lat.toFixed(4)}, {r.lng.toFixed(4)}</span>}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-[10px] text-slate-500">
                      <span className="flex items-center gap-1"><Gauge className="h-2.5 w-2.5" />{spd} km/h</span>
                      {r.dwellSec > 0 && (
                        <span className={`flex items-center gap-1 ${r.stopped && r.dwellSec > 120 ? "text-amber-700" : ""}`}>
                          <Clock className="h-2.5 w-2.5" />dwell {fmtDwell(r.dwellSec)}
                        </span>
                      )}
                      {r.stopped && <span className="rounded bg-amber-100 px-1 text-[9px] uppercase text-amber-700">stopped</span>}
                    </div>
                  </div>
                  <Crosshair className="h-3 w-3 text-slate-400 group-hover:text-slate-700" />
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}

function CamTile({ cam, routeLabel, now }: { cam: { id: string; label: string; recording: boolean; hue: number; tag: string }; routeLabel: string; now: Date }) {
  return (
    <div className="group relative aspect-video overflow-hidden rounded-lg border border-slate-200 bg-black">
      <div className="absolute inset-0" style={{
        background: `radial-gradient(60% 80% at 50% 40%, hsl(${cam.hue} 45% 32%) 0%, hsl(${cam.hue} 30% 14%) 70%, #000 100%)`,
      }} />
      <div className="absolute inset-0 opacity-40 mix-blend-overlay" style={{
        background: `repeating-linear-gradient(0deg, transparent 0 2px, rgba(255,255,255,0.05) 2px 3px)`,
      }} />
      <div className="absolute inset-0 animate-pulse opacity-25" style={{
        background: `linear-gradient(115deg, transparent 30%, hsl(${cam.hue} 70% 65% / 0.3) 50%, transparent 70%)`,
      }} />
      <div className="absolute bottom-3 left-4 right-4 flex items-end gap-1.5">
        {Array.from({ length: 6 }).map((_, i) => (
          <span key={i} className="rounded-t-md bg-black/60" style={{ width: 14 + (i % 3) * 4, height: 22 + ((i * 7) % 18), opacity: 0.55 + (i % 3) * 0.15 }} />
        ))}
      </div>
      <div className="absolute left-1.5 top-1.5 flex items-center gap-1 rounded bg-black/60 px-1.5 py-0.5 font-mono text-[9px] tracking-wider text-white backdrop-blur">
        <span className="font-bold">{cam.tag}</span><span className="text-slate-300">·</span><span>{cam.id}</span>
      </div>
      {cam.recording ? (
        <div className="absolute right-1.5 top-1.5 flex items-center gap-1 rounded bg-rose-500/30 px-1.5 py-0.5 text-[9px] text-white backdrop-blur">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-rose-300" />REC
        </div>
      ) : (
        <div className="absolute right-1.5 top-1.5 rounded bg-slate-500/40 px-1.5 py-0.5 text-[9px] text-white backdrop-blur">OFFLINE</div>
      )}
      <div className="absolute bottom-1 left-1.5 right-1.5 flex items-center justify-between font-mono text-[9px] text-white/90">
        <span>{cam.label}</span>
        <span>{routeLabel} · {now.toLocaleTimeString()}</span>
      </div>
      <span className="absolute left-1 top-1 h-2 w-2 border-l border-t border-white/40" />
      <span className="absolute right-1 top-1 h-2 w-2 border-r border-t border-white/40" />
      <span className="absolute bottom-1 left-1 h-2 w-2 border-b border-l border-white/40" />
      <span className="absolute bottom-1 right-1 h-2 w-2 border-b border-r border-white/40" />
    </div>
  );
}

function RadialGauge({ label, value, max, unit, color, sub }: { label: string; value: number; max: number; unit: string; color: string; sub: string }) {
  const pct = Math.max(0, Math.min(1, value / max));
  const r = 38, c = 2 * Math.PI * r;
  return (
    <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-3">
      <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-500"><span>{label}</span><span>{sub}</span></div>
      <div className="flex items-center gap-3">
        <svg viewBox="0 0 100 100" className="h-20 w-20 -rotate-90">
          <circle cx="50" cy="50" r={r} fill="none" stroke="#e5e7eb" strokeWidth="8" />
          <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
            strokeDasharray={c} strokeDashoffset={c * (1 - pct)} style={{ transition: "stroke-dashoffset 600ms ease" }} />
        </svg>
        <div>
          <div className="font-display text-3xl leading-none text-slate-900">{value}</div>
          <div className="text-[10px] text-slate-500">{unit}</div>
        </div>
      </div>
    </div>
  );
}

function Vital({ icon: I, label, value, tone, bar, flat }: { icon: any; label: string; value: string; tone?: "ok" | "warn"; bar: number; flat?: boolean }) {
  const c = tone === "warn" ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-slate-500"><I className="h-2.5 w-2.5" />{label}</div>
        <div className="font-mono text-[11px] text-slate-900">{value}</div>
      </div>
      {!flat && (
        <div className="mt-1 h-1 overflow-hidden rounded-full bg-slate-100">
          <div className={`h-full ${c}`} style={{ width: `${bar}%`, transition: "width 600ms ease" }} />
        </div>
      )}
    </div>
  );
}

// ============================ ROUTE HISTORY REPLAY (admin-only) ============================
type HistPoint = { id: number; lat: number; lng: number; heading: number | null; speed_kmh: number | null; captured_at: string; next_stop: string | null };

function RouteHistoryDialog({ bus, onClose }: { bus: BusPos | null; onClose: () => void }) {
  const open = !!bus;
  const [windowHrs, setWindowHrs] = useState(6);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(8); // playback multiplier
  const mapDiv = useRef<HTMLDivElement>(null);
  const mapObj = useRef<any>(null);
  const pathLine = useRef<any>(null);
  const traveledLine = useRef<any>(null);
  const marker = useRef<any>(null);
  const stopMarkersRef = useRef<any[]>([]);
  const [roadPath, setRoadPath] = useState<Array<{ lat: number; lng: number }>>([]);
  const [roadLoading, setRoadLoading] = useState(false);

  const { data: points = [], isLoading } = useQuery({
    enabled: open && !!bus,
    queryKey: ["bus-history", bus?.id, windowHrs],
    queryFn: async () => {
      const since = new Date(Date.now() - windowHrs * 3600 * 1000).toISOString();
      const { data, error } = await supabase
        .from("bus_position_history" as any)
        .select("id,lat,lng,heading,speed_kmh,captured_at,next_stop")
        .eq("bus_id", bus!.id)
        .gte("captured_at", since)
        .order("captured_at", { ascending: true })
        .limit(2000);
      if (error) throw error;
      return (data ?? []) as unknown as HistPoint[];
    },
  });

  useEffect(() => { setIdx(0); setPlaying(true); }, [bus?.id, windowHrs, points.length]);

  // Init map
  useEffect(() => {
    if (!open || !mapDiv.current) return;
    let cancelled = false;
    (async () => {
      await loadMapsApi(); const g = window.google.maps;
      if (cancelled || !mapDiv.current) return;
      if (!mapObj.current) {
        mapObj.current = new g.Map(mapDiv.current, {
          center: bus ? { lat: bus.lat, lng: bus.lng } : { lat: 0.3476, lng: 32.5825 },
          zoom: 13,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
      }
    })();
    return () => { cancelled = true; };
  }, [open, bus?.id]);

  // Fetch road-snapped path through sampled history points
  useEffect(() => {
    if (!open || points.length < 2) { setRoadPath([]); return; }
    let cancelled = false;
    setRoadLoading(true);
    (async () => {
      try {
        const { getRoadRoute } = await import("@/lib/mobility-engine");
        // Sample to <= 23 waypoints (Google Routes API limit)
        const maxWp = 23;
        let sampled = points;
        if (points.length > maxWp) {
          const step = Math.ceil(points.length / maxWp);
          sampled = [points[0]];
          for (let i = step; i < points.length - 1; i += step) sampled.push(points[i]);
          sampled.push(points[points.length - 1]);
        }
        const origin = { lat: sampled[0].lat, lng: sampled[0].lng };
        const dest = { lat: sampled[sampled.length - 1].lat, lng: sampled[sampled.length - 1].lng };
        const waypoints = sampled.slice(1, -1).map(p => ({ lat: p.lat, lng: p.lng }));
        const route = await getRoadRoute(origin, dest, waypoints);
        if (!cancelled && route.geometry.length > 1) setRoadPath(route.geometry);
        else if (!cancelled) setRoadPath(points.map(p => ({ lat: p.lat, lng: p.lng })));
      } catch {
        if (!cancelled) setRoadPath(points.map(p => ({ lat: p.lat, lng: p.lng })));
      } finally {
        if (!cancelled) setRoadLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, points]);

  // Draw stop markers (only when route changes)
  useEffect(() => {
    if (!open || !mapObj.current) return;
    let cancelled = false;
    (async () => {
      await loadMapsApi(); if (cancelled || !mapObj.current) return;
      const g = window.google.maps;
      const color = bus ? colorFor(bus.route) : "#0ea5e9";
      stopMarkersRef.current.forEach(m => m.setMap(null));
      stopMarkersRef.current = [];
      const stopDefs = ROUTE_STOPS[bus?.route ?? ""] ?? [];
      stopDefs.forEach((stop, i) => {
        const m = new g.Marker({
          map: mapObj.current,
          position: { lat: stop.lat, lng: stop.lng },
          icon: { path: g.SymbolPath.CIRCLE, scale: 4, fillColor: "#fff", fillOpacity: 1, strokeColor: color, strokeWeight: 2 },
          label: { text: String(i + 1), fontSize: "9px", color: "#1C1917", fontWeight: "bold" },
          zIndex: 1,
        });
        stopMarkersRef.current.push(m);
      });
    })();
    return () => { cancelled = true; };
  }, [open, bus?.route]);

  // Draw full path and current position (uses road-snapped geometry)
  useEffect(() => {
    if (!open || !mapObj.current || points.length === 0) return;
    (async () => {
      await loadMapsApi(); const g = window.google.maps;
      const color = bus ? colorFor(bus.route) : "#0ea5e9";
      const full = roadPath.length > 1 ? roadPath : points.map(p => ({ lat: p.lat, lng: p.lng }));
      // Map raw playback index to road path via distance fraction
      let traveledPath: typeof full;
      if (roadPath.length > 1) {
        let totalRaw = 0;
        for (let i = 1; i < points.length; i++) {
          const a = points[i - 1], b = points[i];
          totalRaw += Math.sqrt((a.lat - b.lat) ** 2 + (a.lng - b.lng) ** 2);
        }
        let traveledRaw = 0;
        for (let i = 1; i <= Math.min(idx, points.length - 1); i++) {
          const a = points[i - 1], b = points[i];
          traveledRaw += Math.sqrt((a.lat - b.lat) ** 2 + (a.lng - b.lng) ** 2);
        }
        const frac = totalRaw > 0 ? traveledRaw / totalRaw : 0;
        const roadIdx = Math.max(1, Math.min(roadPath.length - 1, Math.floor(frac * (roadPath.length - 1)) + 1));
        traveledPath = roadPath.slice(0, roadIdx + 1);
      } else {
        traveledPath = full.slice(0, idx + 1);
      }
      if (!pathLine.current) {
        pathLine.current = new g.Polyline({ map: mapObj.current, path: full, strokeColor: color, strokeOpacity: 0.25, strokeWeight: 5 });
      } else { pathLine.current.setPath(full); pathLine.current.setMap(mapObj.current); }
      if (!traveledLine.current) {
        traveledLine.current = new g.Polyline({ map: mapObj.current, path: traveledPath, strokeColor: color, strokeOpacity: 0.95, strokeWeight: 5,
          icons: [{ icon: { path: g.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2, strokeColor: color }, offset: "100%", repeat: "120px" }] });
      } else { traveledLine.current.setPath(traveledPath); traveledLine.current.setMap(mapObj.current); }
      const cur = points[Math.min(idx, points.length - 1)];
      if (cur) {
        const pos = { lat: cur.lat, lng: cur.lng };
        if (!marker.current) {
          marker.current = new g.Marker({ map: mapObj.current, position: pos, icon: {
            path: "M-10,-6 L10,-6 L12,-4 L12,4 L10,6 L-10,6 L-12,4 L-12,-4 Z",
            fillColor: color, fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2, scale: 1.2, anchor: new g.Point(0, 0), rotation: cur.heading ?? 0,
          } });
        } else {
          marker.current.setPosition(pos);
          const ic = marker.current.getIcon();
          marker.current.setIcon({ ...ic, rotation: cur.heading ?? 0 });
        }
        // Fit bounds first frame
        if (idx === 0 && full.length > 1) {
          const b = new g.LatLngBounds();
          full.forEach(pt => b.extend(pt));
          mapObj.current.fitBounds(b, 60);
        }
      }
    })();
  }, [open, points, idx, bus?.route, roadPath]);

  // Playback ticker
  useEffect(() => {
    if (!open || !playing || points.length === 0) return;
    const id = setInterval(() => {
      setIdx(i => (i + 1 >= points.length ? (setPlaying(false), i) : i + 1));
    }, Math.max(60, 1000 / speed));
    return () => clearInterval(id);
  }, [open, playing, speed, points.length]);

  // Cleanup on close
  useEffect(() => {
    if (open) return;
    pathLine.current?.setMap(null); pathLine.current = null;
    traveledLine.current?.setMap(null); traveledLine.current = null;
    marker.current?.setMap(null); marker.current = null;
    stopMarkersRef.current.forEach(m => m.setMap(null));
    stopMarkersRef.current = [];
    mapObj.current = null;
    setRoadPath([]);
  }, [open]);

  const cur = points[Math.min(idx, Math.max(0, points.length - 1))];
  const distanceKm = useMemo(() => {
    let d = 0;
    for (let i = 1; i <= Math.min(idx, points.length - 1); i++) {
      const a = points[i - 1], b = points[i];
      const R = 6371;
      const dLat = (b.lat - a.lat) * Math.PI / 180;
      const dLng = (b.lng - a.lng) * Math.PI / 180;
      const s = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
      d += 2 * R * Math.asin(Math.sqrt(s));
    }
    return d;
  }, [idx, points]);
  const avgSpeed = useMemo(() => {
    const sub = points.slice(0, idx + 1).filter(p => (p.speed_kmh ?? 0) > 1);
    if (sub.length === 0) return 0;
    return sub.reduce((a, p) => a + (p.speed_kmh ?? 0), 0) / sub.length;
  }, [idx, points]);
  const maxSpeed = useMemo(() => points.slice(0, idx + 1).reduce((m, p) => Math.max(m, p.speed_kmh ?? 0), 0), [idx, points]);
  const durationMin = useMemo(() => {
    if (points.length < 2) return 0;
    const first = new Date(points[0].captured_at).getTime();
    const last = new Date(points[Math.min(idx, points.length - 1)].captured_at).getTime();
    return Math.max(0, Math.round((last - first) / 60000));
  }, [idx, points]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-6xl border-0 bg-white p-0 text-slate-900">
        {bus && (
          <div className="flex flex-col">
            <div className="flex items-center justify-between border-b border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-slate-900 text-white"><HistoryIcon className="h-5 w-5" /></div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-display text-lg">Route history · {bus.route}</h2>
                    <Badge className="bg-slate-900 text-white text-[10px]">ADMIN REPLAY</Badge>
                  </div>
                  <div className="text-[11px] text-slate-500">Trip playback from the append-only telematics log · {points.length} fixes captured</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {[1, 3, 6, 12, 24].map(h => (
                  <Button key={h} size="sm" variant={windowHrs === h ? "default" : "outline"} className="h-7 px-2 text-[11px]" onClick={() => setWindowHrs(h)}>{h}h</Button>
                ))}
                <Button size="icon" variant="ghost" className="ml-1 h-8 w-8" onClick={onClose}><X className="h-4 w-4" /></Button>
              </div>
            </div>

            <div className="grid gap-0 md:grid-cols-[1fr_280px]">
              <div className="relative">
                <div ref={mapDiv} style={{ height: 480 }} className="w-full bg-slate-100" />
                {isLoading && <div className="absolute inset-0 grid place-items-center bg-white/70 text-sm text-slate-600">Loading trip…</div>}
                {roadLoading && !isLoading && points.length > 0 && (
                  <div className="absolute right-3 top-3 rounded-lg border border-slate-200 bg-white/90 px-3 py-1.5 text-[11px] text-slate-600 shadow-sm">Snapping to roads…</div>
                )}
                {!isLoading && points.length === 0 && (
                  <div className="absolute inset-0 grid place-items-center bg-white/80 text-center text-sm text-slate-500">
                    <div>
                      <div className="mx-auto mb-2 grid h-10 w-10 place-items-center rounded-full bg-slate-100"><HistoryIcon className="h-5 w-5 text-slate-400" /></div>
                      No positions logged in the last {windowHrs}h — history is captured automatically each time this bus moves.
                    </div>
                  </div>
                )}
              </div>
              <div className="border-l border-slate-200 bg-slate-50/60 p-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Trip metrics</div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <Metric label="Distance" value={`${distanceKm.toFixed(1)} km`} />
                  <Metric label="Duration" value={`${durationMin} min`} />
                  <Metric label="Avg speed" value={`${avgSpeed.toFixed(0)} km/h`} />
                  <Metric label="Max speed" value={`${maxSpeed.toFixed(0)} km/h`} />
                </div>
                <div className="mt-4 text-[10px] uppercase tracking-wider text-slate-500">Current fix</div>
                <div className="mt-1 rounded-lg border border-slate-200 bg-white p-2 text-[11px]">
                  <div className="font-mono text-slate-700">{cur ? `${cur.lat.toFixed(5)}, ${cur.lng.toFixed(5)}` : "—"}</div>
                  <div className="text-slate-500">{cur ? new Date(cur.captured_at).toLocaleString() : ""}</div>
                  <div className="mt-1 flex items-center justify-between">
                    <span className="text-slate-500">Speed</span>
                    <span className="font-medium text-slate-800">{Math.round(cur?.speed_kmh ?? 0)} km/h</span>
                  </div>
                  <div className="mt-0.5 flex items-center justify-between">
                    <span className="text-slate-500">Heading</span>
                    <span className="font-medium text-slate-800">{Math.round(cur?.heading ?? 0)}°</span>
                  </div>
                  <div className="mt-0.5 flex items-center justify-between">
                    <span className="text-slate-500">Next stop</span>
                    <span className="font-medium text-slate-800">{cur?.next_stop ?? "—"}</span>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-1">
                  <Button size="sm" variant="outline" className="h-8 flex-1 text-xs" onClick={() => setIdx(0)}>
                    <RotateCcw className="mr-1 h-3 w-3" />Restart
                  </Button>
                  <Button size="sm" className="h-8 flex-1 bg-slate-900 text-xs text-white hover:bg-slate-800" onClick={() => setPlaying(p => !p)}>
                    {playing ? <><Pause className="mr-1 h-3 w-3" />Pause</> : <><Play className="mr-1 h-3 w-3" />Play</>}
                  </Button>
                </div>
                <div className="mt-3 text-[10px] uppercase tracking-wider text-slate-500">Playback speed</div>
                <div className="mt-1 flex gap-1">
                  {[2, 4, 8, 16, 32].map(s => (
                    <Button key={s} size="sm" variant={speed === s ? "default" : "outline"} className="h-7 flex-1 text-[11px]" onClick={() => setSpeed(s)}>×{s}</Button>
                  ))}
                </div>
              </div>
            </div>

            <div className="border-t border-slate-200 bg-white px-4 py-3">
              <div className="mb-1 flex items-center justify-between text-[11px] text-slate-500">
                <span>{points[0] ? new Date(points[0].captured_at).toLocaleString() : "—"}</span>
                <span className="font-mono text-slate-700">{cur ? new Date(cur.captured_at).toLocaleTimeString() : "—"}</span>
                <span>{points[points.length - 1] ? new Date(points[points.length - 1].captured_at).toLocaleString() : "—"}</span>
              </div>
              <input
                type="range" min={0} max={Math.max(0, points.length - 1)} value={idx}
                onChange={(e) => { setPlaying(false); setIdx(parseInt(e.target.value, 10)); }}
                className="w-full accent-slate-900"
              />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2">
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-0.5 text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}



